// Web Audio playback engine for parsed MPC1000/JJOS projects.
// Ticks are 96 PPQN (see FORMAT.md). Tuning is in cents (100 cents = 1 semitone).

// Only 0x90 records are real Note On hits. Real projects also contain 0xB0
// (MIDI CC automation, e.g. mod wheel sweeps), 0xE0 (pitch bend automation),
// and 0x8x "marker" records (zero-payload, appear in per-track bursts at
// recording punch-in/loop points) — see FORMAT.md. None of those are notes;
// treating them as one (reading their data bytes as note+velocity) triggers
// bogus extra hits, which is why unfiltered playback sounded glitchy on any
// project containing automation.
export const NOTE_ON_STATUS = 0x90;
export const CC_STATUS = 0xb0;
export const PITCH_BEND_STATUS = 0xe0;

// Master EQ / Master Compressor defaults — one canonical source shared by
// the live constructor, resetMasterInserts, and overrides.ts's initial
// store state, instead of the same literals hand-copied at each site.
export const MASTER_EQ_DEFAULTS = { low: 0, mid: 0, high: 0 };
export const MASTER_COMP_DEFAULTS = { threshold: -24, ratio: 4 };

// Q-Link knob CC assignments (standard MIDI Sound Controller numbers,
// confirmed against a real project — `Mar 30 CHORDS`/track10: 594 CC#74
// events forming a continuous sweep, plus CC#71/73/72, exactly the shape
// of someone riding the MPC1000's real-time Q-Link knobs for Filter
// Cutoff/Resonance/Attack/Decay while playing, not a synth-specific
// mod-wheel). Other CC numbers seen in real data (23, 26) don't have a
// confirmed mapping and are left unmapped rather than guessed.
const CC_FILTER_FREQ = 74;
const CC_FILTER_RES = 71;
const CC_ATTACK = 73;
const CC_DECAY = 72;

// Track Mixer live-automation CC numbers — confirmed by the official JJOS
// 128XL manual (not just statistical inference like the Q-Link numbers
// above): the hardware drives a track's Level/Pan/Send fader live over
// MIDI CC#7/10/91 on channels 1-16 (`mixer.htm`'s "MIDI In
// CC Level/Pan/Send" status line — off by default, but real when a
// project actually rides a fader during recording). Unlike Q-Link
// automation, which reshapes one specific note's sound and is baked in
// per-note, this is track-level and continuous — applied to the track's
// persistent bus (see playSequence), same mechanism as a live user
// mixer edit, and superseded by one if the user makes it this session.
const CC_TRACK_LEVEL = 7;
const CC_TRACK_PAN = 10;
const CC_TRACK_SEND = 91;

/**
 * Build per-track "automation lanes" — sorted {tick, value} arrays — from
 * a sequence's raw event stream, for pitch bend and the Q-Link CC numbers
 * above. Real MIDI CC/pitch-bend is step/hold: a message sets a value that
 * holds until the next one, not something the *source* interpolates — so
 * lookup (see lookupAtOrBefore) just finds the most recent point at or
 * before a given tick, no interpolation.
 */
function buildAutomationLanes(events) {
  const pitchBendByTrack = new Map();
  const ccByTrack = new Map();
  for (const ev of events) {
    if (ev.status === PITCH_BEND_STATUS) {
      // 14-bit value, LSB=data1(`note`)/MSB=data2(`velocity`) per the
      // event record's field layout — standard MIDI pitch bend encoding.
      const raw = ev.note | (ev.velocity << 7);
      // Bend range isn't confirmed for JJOS/MPC1000 (no hardware access to
      // verify) — ±200 cents (2 semitones) is the MIDI GM default and a
      // reasonable approximation, same spirit as the filter/envelope curve
      // approximations already documented above.
      const cents = ((raw - 8192) / 8192) * 200;
      if (!pitchBendByTrack.has(ev.track)) pitchBendByTrack.set(ev.track, []);
      pitchBendByTrack.get(ev.track).push({ tick: ev.tick, value: cents });
    } else if (ev.status === CC_STATUS) {
      let byCC = ccByTrack.get(ev.track);
      if (!byCC) ccByTrack.set(ev.track, (byCC = new Map()));
      let lane = byCC.get(ev.note); // data1 = CC number
      if (!lane) byCC.set(ev.note, (lane = []));
      lane.push({ tick: ev.tick, value: ev.velocity }); // data2 = CC value, 0-127
    }
  }
  return { pitchBendByTrack, ccByTrack };
}

/** Most recent {tick, value} at or before `tick`, or null if automation hasn't started yet. Lanes are already tick-ascending (the event stream itself is sorted). */
function lookupAtOrBefore(lane, tick) {
  if (!lane) return null;
  let result = null;
  for (const point of lane) {
    if (point.tick > tick) break;
    result = point;
  }
  return result;
}

export function buildNoteEventsByBeat(events, ticksPerBeat, beatsPerLoop) {
  const groups = Array.from({ length: beatsPerLoop }, () => []);
  if (!Number.isFinite(ticksPerBeat) || ticksPerBeat <= 0) return groups;
  for (const ev of events ?? []) {
    if (ev.status !== NOTE_ON_STATUS) continue;
    const beatIndex = Math.floor(ev.tick / ticksPerBeat);
    if (beatIndex >= 0 && beatIndex < beatsPerLoop) groups[beatIndex].push(ev);
  }
  return groups;
}

// Short fades avoid the clicks/pops that come from starting or truncating a
// buffer mid-waveform (a standard Web Audio gotcha, independent of the format).
const MIN_FADE_SECONDS = 0.003;
const CHOKE_FADE_SECONDS = 0.008;
const MPC_SAMPLER_DATA_HEADER_SIZE = 20;
const MPC_SAMPLER_CHOP_COUNT = 32;
const MPC_SAMPLER_CHOP_RECORD_SIZE = 8;

function panFrom0to100(value) {
  // MPC convention: 0-49 = left, 50 = center, 51-100 = right (see FORMAT.md).
  return Math.max(-1, Math.min(1, ((value ?? 50) - 50) / 50));
}

// MPC1000/JJOS writes a standard RIFF 'smpl' chunk into WAVs it saves, and
// repurposes it to record playable regions. A single loop entry can represent
// the sample's own Start/End trim. OS2XL/OS3 Non-Destructive Chop can save
// many Chop1-Chop32 start/end windows for one underlying sample either as
// standard smpl loop records or as a JJOS proprietary sampler-data table with
// no loop records. Those windows must stay indexed so the PGM pad's chop
// number can select the right one. AudioContext.decodeAudioData() silently
// discards this chunk, so it has to be read from the raw bytes before decoding.
export function readWavSamplerInfo(arrayBuffer) {
  try {
    const view = new DataView(arrayBuffer);
    if (view.getUint32(0, false) !== 0x52494646 /* 'RIFF' */) return null;
    if (view.getUint32(8, false) !== 0x57415645 /* 'WAVE' */) return null;
    let offset = 12;
    let sampleRate = null;
    let channels = null;
    let bitsPerSample = null;
    let dataLength = null;
    let loops = [];
    while (offset + 8 <= view.byteLength) {
      const id = view.getUint32(offset, false);
      const size = view.getUint32(offset + 4, true);
      const bodyOffset = offset + 8;
      if (id === 0x666d7420 /* 'fmt ' */ && bodyOffset + 16 <= view.byteLength) {
        channels = view.getUint16(bodyOffset + 2, true);
        sampleRate = view.getUint32(bodyOffset + 4, true);
        bitsPerSample = view.getUint16(bodyOffset + 14, true);
      } else if (id === 0x64617461 /* 'data' */) {
        dataLength = size;
      } else if (id === 0x736d706c /* 'smpl' */ && bodyOffset + 36 <= view.byteLength) {
        const numLoops = view.getUint32(bodyOffset + 28, true);
        const samplerDataSize = view.getUint32(bodyOffset + 32, true);
        const loopTableOffset = bodyOffset + 36;
        const maxLoopsByChunk = Math.floor(Math.max(0, size - 36) / 24);
        const readableLoops = Math.min(numLoops, maxLoopsByChunk);
        loops = [];
        for (let i = 0; i < readableLoops; i++) {
          const loopOffset = loopTableOffset + i * 24;
          if (loopOffset + 24 > view.byteLength) break;
          loops.push({
            id: view.getUint32(loopOffset, true),
            type: view.getUint32(loopOffset + 4, true),
            start: view.getUint32(loopOffset + 8, true),
            end: view.getUint32(loopOffset + 12, true),
            fraction: view.getUint32(loopOffset + 16, true),
            playCount: view.getUint32(loopOffset + 20, true),
          });
        }
        const samplerDataOffset = loopTableOffset + readableLoops * 24;
        const samplerDataEnd = samplerDataOffset + samplerDataSize;
        const chopTableOffset = samplerDataOffset + MPC_SAMPLER_DATA_HEADER_SIZE;
        const chopTableSize = MPC_SAMPLER_CHOP_COUNT * MPC_SAMPLER_CHOP_RECORD_SIZE;
        const samplerDataLooksLikeJjosWav =
          samplerDataSize >= 2 &&
          samplerDataOffset + 2 <= bodyOffset + size &&
          view.getUint16(samplerDataOffset, true) === 2;
        if (
          samplerDataSize >= MPC_SAMPLER_DATA_HEADER_SIZE + chopTableSize &&
          samplerDataLooksLikeJjosWav &&
          samplerDataEnd <= bodyOffset + size &&
          chopTableOffset + chopTableSize <= view.byteLength
        ) {
          loops = Array.from({ length: MPC_SAMPLER_CHOP_COUNT }, (_, i) => {
            const recordOffset = chopTableOffset + i * MPC_SAMPLER_CHOP_RECORD_SIZE;
            return {
              id: i + 1,
              type: 0,
              start: view.getUint32(recordOffset, true),
              end: view.getUint32(recordOffset + 4, true),
              fraction: 0,
              playCount: 0,
              source: 'samplerData',
            };
          });
        }
      }
      offset = bodyOffset + size + (size % 2);
    }
    if (!sampleRate || !channels || !bitsPerSample || !dataLength || !loops.length) return null;

    const totalFrames = dataLength / ((bitsPerSample / 8) * channels);
    const toTrim = (loop) => {
      if (!loop || loop.end <= loop.start || loop.start >= totalFrames) return null;
      const endFrame = Math.min(loop.end, totalFrames);
      if (endFrame <= loop.start) return null;
      return {
        offsetSeconds: loop.start / sampleRate,
        durationSeconds: (endFrame - loop.start) / sampleRate,
      };
    };

    // Multiple smpl loop records are how OS2XL/OS3 can persist several
    // non-destructive chop windows against one WAV. Do not collapse the
    // first loop into a global sample trim; pad playback will index into
    // this table with pad.chopIndex.
    if (loops.length > 1) {
      const chopTrims = loops.map(toTrim);
      return {
        trim: null,
        chopTrims,
        loopCount: loops.length,
        loops,
      };
    }

    const loopStart = loops[0].start;
    const loopEnd = loops[0].end;
    const trim = toTrim(loops[0]);
    if (!trim) return null;

    // A standard WAV smpl-chunk loop is a *sustain-loop* marker (play from
    // the start; only loop the marked region if a note is held past it),
    // not a hard "discard everything outside this" instruction — that's
    // only true when a real MPC Start/End trim wrote it. Real trims
    // (confirmed on files with no other explanation) keep a substantial
    // middle/majority portion of the file; a loop region under ~20% of the
    // total length is instead the signature of a genuine synth-style
    // sustain loop baked into factory/library content long before the
    // file ever touched an MPC — found via a real "track came out super
    // quiet" report traced to `THMB40-60.WAV` (JJOS's own bundled thumb-
    // piano demo kit): identical loopStart/loopEnd on every copy across
    // three unrelated projects (APR 30 RECORD, Mar 30 CHORDS, NOV 14),
    // always the last ~4-12% of the file — a per-project user trim
    // wouldn't reproduce byte-for-byte across independent projects, a
    // shared factory asset would. A full library sweep (433 real WAVs
    // with a smpl loop) found a clean split either side of ~20-40%: 401
    // sit at 40%+ (a plausible trim), 30 sit under 20% (all matching this
    // pattern) — no files fall in between, so this isn't a fragile cutoff.
    const loopLengthRatio = (loopEnd - loopStart) / totalFrames;
    // Official Akai factory WAVs also use smpl loops for ordinary sustain
    // metadata: several full-length keyboard/filter samples mark a region
    // from somewhere in the body almost to EOF, which should still play
    // from the file start. Treating that as MPC Start/End trim makes those
    // sounds preview as late snippets and makes sequence playback feel
    // silent/blippy. A real hard trim should end clearly before EOF.
    const loopEndsAtFileEnd = Math.abs(totalFrames - loopEnd) <= Math.max(32, totalFrames * 0.01);
    if (loopEndsAtFileEnd || loopLengthRatio < 0.2) return null;

    return { trim, chopTrims: null, loopCount: 1, loops };
  } catch {
    return null;
  }
}

export function readWavTrim(arrayBuffer) {
  return readWavSamplerInfo(arrayBuffer)?.trim ?? null;
}

// --- Envelope/filter mappings ---
// PGM stores attack/decay/filter-freq/filter-res/vel-to-freq as abstract 0-100 knob
// values; the hardware's exact response curves aren't reverse-engineered
// (see FORMAT.md). These are reasonable approximations, not authoritative:
// exponential time curves (most of a hardware envelope knob's useful range
// is bunched near the low end) and a log frequency sweep (matches how pitch
// is perceived). Zero-valued fields (the common case — most kits never touch
// these) always mean "no shaping," so this only changes sound where a kit
// actually specifies non-default attack/decay/filter values.
function clamp0to100(value, fallback = 0) {
  const n = Number.isFinite(value) ? value : fallback;
  return Math.max(0, Math.min(100, n));
}
function attackSeconds(attack) {
  if (!attack) return 0;
  return Math.pow(attack / 100, 2) * 1.5; // 0-100 -> 0-1.5s
}
export function decaySeconds(decay, decayMode = 0) {
  if (!decay || decayMode !== 1) return 0;
  return Math.pow(decay / 100, 2) * 3; // 0-100 -> 0-3s
}
// JJOS Sustain/Release pair (PGM sustain@0x69/release@0x6a — see FORMAT.md
// and pgm.js) — real ADSR fields the engine never implemented before.
// Time scale reuses decaySeconds' own curve (0-100 -> 0-3s quadratic) as
// the only consistent, already-in-use assumption available; the exact
// real-hardware time mapping for Release hasn't been independently
// measured yet, so this is a first-pass estimate, not a calibrated value.
function releaseSeconds(release) {
  return Math.pow(clamp0to100(release, 0) / 100, 2) * 3;
}
function filterFreqHz(freq) {
  const min = 80;
  const max = 16000;
  return min * Math.pow(max / min, clamp0to100(freq, 100) / 100);
}
function filterQ(res) {
  return 0.7 + (clamp0to100(res, 0) / 100) * 17.3; // 0-100 -> 0.7 (flat) - 18 (near self-oscillating)
}
function velocityModulatedFilterFreq(freq, amount, velocity = 127) {
  const base = clamp0to100(freq, 100);
  const depth = clamp0to100(amount, 0);
  if (!depth) return base;
  // MPC's V>Freq makes cutoff depend on hit velocity. Without hardware curve
  // data, treat the stored Freq as the full-velocity cutoff and let softer
  // hits pull it down by the V>Freq amount.
  const velNorm = Math.max(0, Math.min(1, (velocity ?? 127) / 127));
  return clamp0to100(base - depth * (1 - velNorm), base);
}
// Same idea as velocityModulatedFilterFreq but for the pad's Vel>Level
// amount (PGM velocityToLevel@0x6b): 0 = hits always play at the pad's set
// level regardless of hit strength, 100 = full velocity sensitivity.
// Fallback of 100 (full sensitivity) matches this engine's pre-existing
// behavior for callers that don't have a real parsed pad (e.g. synthetic
// preview pads without a velocityToLevel byte).
//
// Curve is linear-in-dB (floor at min velocity, unity at velocity 127),
// not linear-in-amplitude: perceived loudness is roughly logarithmic, so a
// straight velocity/127 amplitude ramp (this function's previous behavior)
// compresses the audible difference between soft and hard hits well below
// what the real MPC1000 produces. No hardware A/B calibration data exists
// for this curve yet (unlike padLevelGain above), so -40dB is a reasonable
// estimate for the floor, not a measured value.
const VELOCITY_GAIN_FLOOR_DB = -40;
function velocityModulatedGain(amount, velocity = 127) {
  const depth = clamp0to100(amount, 100);
  const velNorm = Math.max(0, Math.min(1, (velocity ?? 127) / 127));
  const db = VELOCITY_GAIN_FLOOR_DB * (1 - velNorm) * (depth / 100);
  return Math.pow(10, db / 20);
}
// Pad Mixer Level (0-100) is confirmed NOT a linear amplitude control on
// the real MPC1000. Calibrated with two real hardware A/B points, both on
// pad `dr55-sd11` through the same signal path, done directly in this app
// (matching this player's own Level slider by ear against the hardware,
// not a guessed dB estimate): hardware Level=90 matched this app's old
// flat level/100 scale set to Level=30 (-10.46dB), and hardware Level=30
// matched this app's Level=5 (-26.02dB). A first attempt fit a single
// constant dB-per-step slope from only the L=90 point (anchored at
// Level=100=unity) and extrapolated it down to L=30 — that overshot to
// ~-73dB, audibly silent, because the real slope is ~4x steeper between
// 90-100 (~1.05dB/unit) than between 30-90 (~0.26dB/unit): not a single
// constant-dB curve. Modeled here as piecewise-linear-in-dB through the
// two measured points plus the untouched-default anchor at Level=100 (the
// vast majority of real pads sit at this default, so it's kept fixed as
// unity/0dB rather than fit). The 0-30 segment has no real calibration
// point yet — extrapolated by continuing the 30-90 segment's slope rather
// than assuming a steeper drop, to avoid repeating the previous overshoot.
const PAD_LEVEL_DB_POINTS = [
  [0, 20 * Math.log10(0.05) - 30 * ((20 * Math.log10(0.3) - 20 * Math.log10(0.05)) / 60)],
  [30, 20 * Math.log10(0.05)], // measured: matches real hardware Level=30
  [90, 20 * Math.log10(0.3)], // measured: matches real hardware Level=90
  [100, 0], // untouched-default anchor = unity
];
function padLevelGain(level) {
  const L = clamp0to100(level, 100);
  let lo = PAD_LEVEL_DB_POINTS[0];
  let hi = PAD_LEVEL_DB_POINTS[PAD_LEVEL_DB_POINTS.length - 1];
  for (let i = 0; i < PAD_LEVEL_DB_POINTS.length - 1; i++) {
    if (L >= PAD_LEVEL_DB_POINTS[i][0] && L <= PAD_LEVEL_DB_POINTS[i + 1][0]) {
      [lo, hi] = [PAD_LEVEL_DB_POINTS[i], PAD_LEVEL_DB_POINTS[i + 1]];
      break;
    }
  }
  const t = hi[0] === lo[0] ? 0 : (L - lo[0]) / (hi[0] - lo[0]);
  const db = lo[1] + t * (hi[1] - lo[1]);
  return Math.pow(10, db / 20);
}
function filterAttenuationGain(value) {
  // PGM format: 0 = 0dB, 1 = -6dB, 2 = -12dB. Unknown/out-of-range values
  // are ignored rather than treated as arbitrary gain reductions.
  if (value === 1) return Math.pow(10, -6 / 20);
  if (value === 2) return Math.pow(10, -12 / 20);
  return 1;
}
const FILTER_TYPES = { 1: 'lowpass', 2: 'bandpass', 3: 'highpass' };

/**
 * Default override resolver: just reads straight from the parsed file data,
 * no live user edits. Passing a partial object into playSequence/previewPad
 * merges over these — see useOverridesStore for the Pinia-backed version
 * actually used by the app. Kept here (not in the Pinia layer) so this file
 * stays framework-agnostic and works standalone.
 */
const DEFAULT_OVERRIDES = {
  getPadLevel: (programName, pad) => pad?.mixerLevel ?? 100,
  getPadPan: (programName, pad) => pad?.mixerPan ?? 50,
  getPadTuningOffset: () => 0,
  getPadAttack: (programName, pad) => pad?.attack ?? 0,
  getPadDecay: (programName, pad) => pad?.decay ?? 0,
  getPadSustain: (programName, pad) => pad?.sustain ?? 100,
  getPadRelease: (programName, pad) => pad?.release ?? 0,
  getPadPlayModeOverride: () => null,
  getPadReverse: (programName, pad) => !!pad?.reverse,
  getChopMode: () => 'equal',
  isFxEnabled: () => false,
  getTrackLevel: (trackNumber, track) => track?.level ?? 100,
  getTrackPan: (trackNumber, track) => track?.pan ?? 50,
  getTrackSend: (trackNumber, track) => track?.send ?? 30,
  isTrackAudible: (trackNumber, track) => !track?.muted,
  getVarispeed: () => 1,
  hasTrackLevelOverride: () => false,
  hasTrackPanOverride: () => false,
  hasTrackSendOverride: () => false,
};

export class AudioEngine {
  static PREVIEW_CHOKE_KEY = '__preview__';

  // Assigned inside _wireMasterChain (called from the constructor), not
  // inline in the constructor body itself — TS's checkJs can't follow
  // that assignment through a called method, so it infers these as
  // possibly-undefined without an explicit declared type here.
  /** @type {DynamicsCompressorNode} */
  limiter;
  /** @type {ReturnType<AudioEngine['_createMasterInsert']>} */
  masterEq;
  /** @type {ReturnType<AudioEngine['_createMasterInsert']>} */
  masterComp;

  constructor() {
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.9;
    this._wireMasterChain(this.ctx.destination);
    // Shared FX send bus (Trk Mixer's "Send" row / CC#91 — see FORMAT.md):
    // every track bus can tap a post-fader send into this one shared bus,
    // mirroring a real send/return bus rather than each track having its
    // own effect instance. Off by default — see the `Fx` toggle in
    // TrkMixer.vue and isFxEnabled below. Deliberately NOT wired to the
    // file's stored send level by default: that field's real-world
    // default (30 on ~99% of sampled tracks — see FORMAT.md) has never
    // been acted on by any past version of this app, so treating it as
    // "on" out of the box would quietly add an audible effect tail to
    // every existing project's sound rather than reproducing something
    // already there.
    //
    // Fans out into two independently-typed parallel slots — FX1 and
    // FX2, matching JJOS's own two-slot onboard FX rack (stock Akai OS
    // has no onboard effects at all; JJOS added this exact FX1/FX2 split
    // — see the effect type lists in _buildFxEffect below). The real
    // hardware lets a track's Output field pick *which one* of FX1/FX2 it
    // sends to; that byte's mapping isn't reliably decodable (see
    // REVERSE_ENGINEERING_PLAN.md §2), so both slots here just receive
    // the same confirmed per-track Send amount in parallel rather than
    // guessing a per-track destination.
    this.fxSendBus = this.ctx.createGain();
    this.fx1 = this._createFxSlot(this.master);
    this.fx2 = this._createFxSlot(this.master);
    this.fxSendBus.connect(this.fx1.input);
    this.fxSendBus.connect(this.fx2.input);
    this.fx1.setType('reverb', { decay: 1.8 });
    /** @type {Map<string, AudioBuffer>} keyed by uppercased sample name, no extension */
    this.buffers = new Map();
    /** @type {WeakMap<AudioBuffer, AudioBuffer>} lazily-built reversed copies for pad reverse playback. */
    this._reversedBuffers = new WeakMap();
    /** @type {Map<string, {offsetSeconds: number, durationSeconds: number}>} same keys as `buffers` — only set when the WAV carries an explicit trim (see readWavTrim) */
    this.sampleTrims = new Map();
    /** @type {Map<string, ({offsetSeconds: number, durationSeconds: number}|null)[]>} multi-loop smpl entries for OS2XL/OS3 Non-Destructive Chop, keyed like `buffers`. */
    this.sampleChopTrims = new Map();
    /**
     * Currently-sounding voices keyed by choke group, so a new hit can cut
     * off whatever it should choke (MPC "mute group" / mono voice overlap).
     * @type {Map<string, { gainNode: GainNode, src: AudioBufferSourceNode }>}
     */
    this.voices = new Map();
    /** @type {{name: string, error: string}[]} WAVs that failed to decode — surfaced in the Debug panel rather than only a console.warn, since a silently-missing buffer is otherwise very hard to notice. */
    this.decodeWarnings = [];
    /** @type {Map<string, number[]|null>} onset-boundary cache, keyed by `${sampleKey}::${sliceCount}` — see _onsetChopBoundaries. */
    this._onsetBoundaryCache = new Map();
  }

  static keyFor(sampleName) {
    return sampleName.trim().toUpperCase().replace(/\.WAV$/, '');
  }

  /**
   * A simple synthetic "hall"-style impulse response for the shared FX
   * send bus's ConvolverNode: exponentially-decaying stereo noise. No
   * real IR sample is bundled with this app, and this is a standard,
   * well-known technique for a basic algorithmic reverb without one.
   */
  _createSyntheticImpulseResponse(durationSeconds = 1.8, decayPower = 3) {
    const sampleRate = this.ctx.sampleRate;
    const length = Math.floor(sampleRate * durationSeconds);
    const impulse = this.ctx.createBuffer(2, length, sampleRate);
    for (let ch = 0; ch < impulse.numberOfChannels; ch++) {
      const data = impulse.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decayPower);
      }
    }
    return impulse;
  }

  /**
   * master -> masterEq -> masterComp -> limiter -> destination. Shared
   * between the live constructor and the offline bounce path (see
   * renderSequenceOffline) — same reason `_createBus` is shared for the
   * rest of the chain — so a future change to this topology (reordering
   * the inserts, adding a third one) can't silently apply to only one of
   * the two.
   *
   * The limiter is a peak safety net, not an always-on bus compressor —
   * see the old constructor comment this absorbed: many simultaneous
   * full-velocity hits (e.g. a busy drum pattern) can otherwise sum past
   * 0dB and clip/distort. Threshold sits close to 0dB on purpose. At
   * -6dB/20:1 (an old setting) it engaged on essentially every normal-
   * loudness hit and, at 20:1, crushed a 2dB pre-limiter difference down
   * to ~0.15dB post-limiter — audibly erasing real pad/track Level
   * differences (e.g. a Level=127 track sounding barely different from
   * Level=100) that the real MPC1000's plain unlimited summing bus
   * doesn't touch.
   *
   * `eqDefaults`/`compDefaults` default to the fresh-session values
   * (`MASTER_EQ_DEFAULTS`/`MASTER_COMP_DEFAULTS`); the offline bounce
   * path passes the *live* instance's current params instead, so an
   * export starts from what's actually playing rather than discarding a
   * throwaway default object immediately after construction.
   */
  _wireMasterChain(destination, { eqDefaults = MASTER_EQ_DEFAULTS, compDefaults = MASTER_COMP_DEFAULTS } = {}) {
    this.limiter = this.ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -1;
    this.limiter.knee.value = 0;
    this.limiter.ratio.value = 20;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.1;
    // Master EQ / Master Compressor — JJOS's own master-bus processing,
    // the "FX" screen right above FX1/FX2 in the OS manual (a 3-band EQ
    // and a compressor sitting on the whole summed mix, distinct from
    // the parallel send-bus effects below). Both start disabled/bypassed
    // — same "off by default" rule as the rest of this rack, see
    // _createMasterInsert.
    this.masterEq = this._createMasterInsert('eq', eqDefaults);
    this.masterComp = this._createMasterInsert('compressor', compDefaults);
    this.master.connect(this.masterEq.input);
    this.masterEq.output.connect(this.masterComp.input);
    this.masterComp.output.connect(this.limiter);
    this.limiter.connect(destination);
  }

  /**
   * One swappable slot on the FX send bus (see the constructor) — `input`
   * is a fixed node the caller wires up once; `setType` tears down
   * whatever effect is currently built and rebuilds a new one from
   * `_buildFxEffect`, while `setParam` tweaks the live params of whatever
   * type is currently active without rebuilding the graph (so a slider
   * drag doesn't retrigger LFOs/impulse responses on every tick). Reused
   * as-is for the offline bounce context in renderSequenceOffline via
   * `Object.create(AudioEngine.prototype)`, same pattern as `_createBus`.
   *
   * `input` permanently fans out two ways: to `dryGain` (always connected
   * straight to `output`) and to `effectInput` (whatever `_buildFxEffect`
   * builds each `setType`, disposed/rebuilt on type swap same as before).
   * The `mix` param (0-100, handled here rather than in `_buildFxEffect`
   * since it's a slot-level knob, not part of any one effect's own DSP)
   * crossfades `dryGain`/`wetGain` — 100 (the default, matching every
   * slot's behavior before this existed) means the slot outputs only the
   * processed effect, same as before; lower values blend in more of
   * whatever was actually sent to this slot, unprocessed.
   */
  _createFxSlot(output) {
    const engine = this;
    const input = this.ctx.createGain();
    const effectInput = this.ctx.createGain();
    const dryGain = this.ctx.createGain();
    const wetGain = this.ctx.createGain();
    input.connect(effectInput);
    input.connect(dryGain).connect(output);
    wetGain.connect(output);
    function applyMix(mixPct) {
      const wet = Math.max(0, Math.min(100, mixPct)) / 100;
      const now = engine.ctx.currentTime;
      wetGain.gain.setTargetAtTime(wet, now, 0.01);
      dryGain.gain.setTargetAtTime(1 - wet, now, 0.01);
    }
    applyMix(100);
    let active = null;
    const slot = {
      input,
      type: 'off',
      params: {},
      setType(type, params = {}) {
        if (active) active.dispose();
        effectInput.disconnect();
        slot.type = type;
        slot.params = params;
        applyMix(params.mix ?? 100);
        active = engine._buildFxEffect(type, effectInput, wetGain, params);
      },
      setParam(name, value) {
        slot.params = { ...slot.params, [name]: value };
        if (name === 'mix') {
          applyMix(value);
          return;
        }
        active?.setParam?.(name, value);
      },
    };
    return slot;
  }

  /**
   * A single stage inserted directly in-line on the master bus (unlike
   * _createFxSlot's parallel send-bus effects) — used for Master EQ /
   * Master Compressor. Reuses _buildFxEffect for the actual DSP. When
   * disabled, `input` connects straight through to `output` — a true
   * bypass rather than just neutral params, so a disabled insert can't
   * color the sound at all.
   */
  _createMasterInsert(type, defaultParams) {
    const engine = this;
    const input = this.ctx.createGain();
    const output = this.ctx.createGain();
    let active = null;
    let pendingSwap = null;
    const RAMP_SECONDS = 0.006;
    const swapTopology = (value) => {
      if (active) {
        active.dispose();
        active = null;
      }
      input.disconnect();
      if (value) active = engine._buildFxEffect(type, input, output, insert.params);
      else input.connect(output);
    };
    const insert = {
      input,
      output,
      enabled: false,
      params: { ...defaultParams },
      /**
       * `instant` skips the click-avoidance ramp below and swaps the
       * topology synchronously — for setup that hasn't carried any real
       * audio yet (nothing to click), not a live transition. Required
       * for the offline bounce path specifically: an OfflineAudioContext
       * doesn't advance in real time, so the setTimeout this uses to
       * defer the live swap (see below) would land at an unrelated point
       * in the render rather than actually ~6ms into the rendered audio.
       */
      setEnabled(value, { instant = false } = {}) {
        if (insert.enabled === value) return;
        insert.enabled = value;
        if (instant) {
          // A live (non-instant) toggle may still have a deferred swap
          // in flight — e.g. a session reset landing within the ramp
          // window of a toggle that just happened. Left uncancelled,
          // that stale timer fires later and both re-applies the old
          // enabled state (silently reverting this instant change) and
          // resumes wherever it left output.gain's automation timeline,
          // which can also just leave gain parked mid-fade. Cancel it
          // and snap gain back to steady-state before swapping.
          if (pendingSwap) {
            clearTimeout(pendingSwap);
            pendingSwap = null;
          }
          output.gain.cancelScheduledValues(engine.ctx.currentTime);
          output.gain.value = 1;
          swapTopology(value);
          return;
        }
        // `output` feeds the rest of the master bus — literally every
        // track/voice in the engine routes through it — so an unmasked
        // topology swap is an audible click on the whole mix, not just
        // this insert. connect()/disconnect() take effect immediately,
        // not on the AudioParam timeline, so fading `output.gain` down
        // and doing the swap in the same tick (as an earlier version of
        // this did) doesn't actually mask it — the swap's own
        // discontinuity still lands while gain is still ~1. Deferring
        // the swap with setTimeout until the fade-down has (roughly)
        // completed is what actually gets it under the masked window.
        const now = engine.ctx.currentTime;
        output.gain.cancelScheduledValues(now);
        output.gain.setValueAtTime(output.gain.value, now);
        output.gain.linearRampToValueAtTime(0, now + RAMP_SECONDS);
        if (pendingSwap) clearTimeout(pendingSwap);
        pendingSwap = setTimeout(() => {
          pendingSwap = null;
          swapTopology(value);
          const swapNow = engine.ctx.currentTime;
          output.gain.cancelScheduledValues(swapNow);
          output.gain.setValueAtTime(0, swapNow);
          output.gain.linearRampToValueAtTime(1, swapNow + RAMP_SECONDS);
        }, RAMP_SECONDS * 1000);
      },
      setParam(name, value) {
        insert.params = { ...insert.params, [name]: value };
        active?.setParam?.(name, value);
      },
      /**
       * Session-boundary reset (see overrides.ts resetAll) — bypasses and
       * drops back to defaults, not just disabled with stale params
       * leftover from a previous project. `instant` — a bulk reset isn't
       * a live transition to mask, and playback is already stopped by
       * the time this runs (see resetRuntime in project.ts).
       */
      reset(defaults) {
        insert.params = { ...defaults };
        insert.setEnabled(false, { instant: true });
      },
    };
    input.connect(output);
    return insert;
  }

  /**
   * FX1 (Reverb/Delay/EQ/Compressor/Phaser/Tremolo/Auto Pan/Bit Grunge)
   * and FX2 (Chorus/Flanger) effect types, matching JJOS's own onboard FX
   * menu split (see FxWindow.vue's FX_TYPES for the same list surfaced in
   * the UI, and the constructor comment above for why both slots share
   * one input). Every type here connects `input` through to `output`
   * itself and returns `{ setParam(name, value), dispose() }`; `type ===
   * 'off'` (or anything unrecognized) returns null and leaves the slot
   * silent.
   */
  _buildFxEffect(type, input, output, params = {}) {
    const ctx = this.ctx;
    switch (type) {
      case 'reverb': {
        let decay = params.decay ?? 1.8;
        const convolver = ctx.createConvolver();
        convolver.buffer = this._createSyntheticImpulseResponse(decay);
        input.connect(convolver).connect(output);
        return {
          setParam: (name, value) => {
            if (name !== 'decay') return;
            decay = value;
            convolver.buffer = this._createSyntheticImpulseResponse(decay);
          },
          dispose: () => convolver.disconnect(),
        };
      }
      case 'delay': {
        const delay = ctx.createDelay(2);
        delay.delayTime.value = params.time ?? 0.3;
        const feedback = ctx.createGain();
        feedback.gain.value = params.feedback ?? 0.35;
        input.connect(delay);
        delay.connect(feedback).connect(delay);
        delay.connect(output);
        return {
          setParam: (name, value) => {
            if (name === 'time') delay.delayTime.setTargetAtTime(value, ctx.currentTime, 0.01);
            if (name === 'feedback') feedback.gain.setTargetAtTime(Math.min(0.9, value), ctx.currentTime, 0.01);
          },
          dispose: () => {
            delay.disconnect();
            feedback.disconnect();
          },
        };
      }
      case 'eq': {
        const low = ctx.createBiquadFilter();
        low.type = 'lowshelf';
        low.frequency.value = 200;
        const mid = ctx.createBiquadFilter();
        mid.type = 'peaking';
        mid.frequency.value = 1000;
        mid.Q.value = 0.8;
        const high = ctx.createBiquadFilter();
        high.type = 'highshelf';
        high.frequency.value = 5000;
        low.gain.value = params.low ?? 0;
        mid.gain.value = params.mid ?? 0;
        high.gain.value = params.high ?? 0;
        input.connect(low).connect(mid).connect(high).connect(output);
        return {
          setParam: (name, value) => {
            const node = name === 'low' ? low : name === 'mid' ? mid : name === 'high' ? high : null;
            node?.gain.setTargetAtTime(value, ctx.currentTime, 0.01);
          },
          dispose: () => {
            low.disconnect();
            mid.disconnect();
            high.disconnect();
          },
        };
      }
      case 'compressor': {
        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = params.threshold ?? -24;
        comp.ratio.value = params.ratio ?? 4;
        comp.attack.value = 0.005;
        comp.release.value = 0.15;
        input.connect(comp).connect(output);
        return {
          setParam: (name, value) => {
            if (name === 'threshold') comp.threshold.setTargetAtTime(value, ctx.currentTime, 0.01);
            if (name === 'ratio') comp.ratio.setTargetAtTime(value, ctx.currentTime, 0.01);
          },
          dispose: () => comp.disconnect(),
        };
      }
      case 'phaser': {
        const stages = Array.from({ length: 4 }, () => {
          const f = ctx.createBiquadFilter();
          f.type = 'allpass';
          f.frequency.value = 800;
          return f;
        });
        input.connect(stages[0]);
        for (let i = 0; i < stages.length - 1; i++) stages[i].connect(stages[i + 1]);
        stages[stages.length - 1].connect(output);
        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.value = params.rate ?? 0.5;
        const depthGain = ctx.createGain();
        depthGain.gain.value = params.depth ?? 600;
        lfo.connect(depthGain);
        for (const stage of stages) depthGain.connect(stage.frequency);
        lfo.start();
        return {
          setParam: (name, value) => {
            if (name === 'rate') lfo.frequency.setTargetAtTime(value, ctx.currentTime, 0.01);
            if (name === 'depth') depthGain.gain.setTargetAtTime(value, ctx.currentTime, 0.01);
          },
          dispose: () => {
            lfo.stop();
            lfo.disconnect();
            depthGain.disconnect();
            for (const s of stages) s.disconnect();
          },
        };
      }
      case 'tremolo': {
        let depth = params.depth ?? 0.5;
        const trem = ctx.createGain();
        trem.gain.value = 1 - depth / 2;
        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.value = params.rate ?? 5;
        const lfoGain = ctx.createGain();
        lfoGain.gain.value = depth / 2;
        lfo.connect(lfoGain).connect(trem.gain);
        lfo.start();
        input.connect(trem).connect(output);
        return {
          setParam: (name, value) => {
            if (name === 'rate') lfo.frequency.setTargetAtTime(value, ctx.currentTime, 0.01);
            if (name === 'depth') {
              depth = value;
              trem.gain.setTargetAtTime(1 - depth / 2, ctx.currentTime, 0.01);
              lfoGain.gain.setTargetAtTime(depth / 2, ctx.currentTime, 0.01);
            }
          },
          dispose: () => {
            lfo.stop();
            lfo.disconnect();
            lfoGain.disconnect();
            trem.disconnect();
          },
        };
      }
      case 'autopan': {
        const panner = ctx.createStereoPanner();
        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.value = params.rate ?? 1;
        const lfoGain = ctx.createGain();
        lfoGain.gain.value = params.depth ?? 0.8;
        lfo.connect(lfoGain).connect(panner.pan);
        lfo.start();
        input.connect(panner).connect(output);
        return {
          setParam: (name, value) => {
            if (name === 'rate') lfo.frequency.setTargetAtTime(value, ctx.currentTime, 0.01);
            if (name === 'depth') lfoGain.gain.setTargetAtTime(Math.min(1, value), ctx.currentTime, 0.01);
          },
          dispose: () => {
            lfo.stop();
            lfo.disconnect();
            lfoGain.disconnect();
            panner.disconnect();
          },
        };
      }
      case 'bitcrush': {
        const shaper = ctx.createWaveShaper();
        const setDepth = (bits) => {
          const steps = Math.pow(2, Math.max(1, Math.min(16, bits)));
          const curve = new Float32Array(1024);
          for (let i = 0; i < curve.length; i++) {
            const x = (i / (curve.length - 1)) * 2 - 1;
            curve[i] = Math.round(x * steps) / steps;
          }
          shaper.curve = curve;
        };
        setDepth(params.depth ?? 5);
        input.connect(shaper).connect(output);
        return {
          setParam: (name, value) => {
            if (name === 'depth') setDepth(value);
          },
          dispose: () => shaper.disconnect(),
        };
      }
      case 'chorus': {
        const dry = ctx.createGain();
        dry.gain.value = 0.6;
        const wet = ctx.createGain();
        wet.gain.value = 0.6;
        const delay = ctx.createDelay(0.05);
        delay.delayTime.value = 0.02;
        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.value = params.rate ?? 1.2;
        const lfoGain = ctx.createGain();
        lfoGain.gain.value = params.depth ?? 0.006;
        lfo.connect(lfoGain).connect(delay.delayTime);
        lfo.start();
        input.connect(dry).connect(output);
        input.connect(delay);
        delay.connect(wet).connect(output);
        return {
          setParam: (name, value) => {
            if (name === 'rate') lfo.frequency.setTargetAtTime(value, ctx.currentTime, 0.01);
            if (name === 'depth') lfoGain.gain.setTargetAtTime(value, ctx.currentTime, 0.01);
          },
          dispose: () => {
            lfo.stop();
            lfo.disconnect();
            lfoGain.disconnect();
            delay.disconnect();
            dry.disconnect();
            wet.disconnect();
          },
        };
      }
      case 'flanger': {
        const dry = ctx.createGain();
        dry.gain.value = 0.7;
        const wet = ctx.createGain();
        wet.gain.value = 0.7;
        const delay = ctx.createDelay(0.02);
        delay.delayTime.value = 0.003;
        const feedback = ctx.createGain();
        feedback.gain.value = params.feedback ?? 0.4;
        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.value = params.rate ?? 0.25;
        const lfoGain = ctx.createGain();
        lfoGain.gain.value = params.depth ?? 0.002;
        lfo.connect(lfoGain).connect(delay.delayTime);
        lfo.start();
        input.connect(dry).connect(output);
        input.connect(delay);
        delay.connect(feedback).connect(delay);
        delay.connect(wet).connect(output);
        return {
          setParam: (name, value) => {
            if (name === 'rate') lfo.frequency.setTargetAtTime(value, ctx.currentTime, 0.01);
            if (name === 'depth') lfoGain.gain.setTargetAtTime(value, ctx.currentTime, 0.01);
            if (name === 'feedback') feedback.gain.setTargetAtTime(Math.min(0.9, value), ctx.currentTime, 0.01);
          },
          dispose: () => {
            lfo.stop();
            lfo.disconnect();
            lfoGain.disconnect();
            delay.disconnect();
            feedback.disconnect();
            dry.disconnect();
            wet.disconnect();
          },
        };
      }
      default:
        return null;
    }
  }

  /** FX Settings window — swap the effect type running in slot `slot` ('fx1'|'fx2'). See _buildFxEffect for the type list and _createFxSlot for the swap mechanics. */
  setFxType(slot, type, params = {}) {
    (slot === 'fx2' ? this.fx2 : this.fx1).setType(type, params);
  }

  /** FX Settings window — live-tweak one param of whatever effect is currently active in slot `slot`, no graph rebuild. */
  setFxParam(slot, name, value) {
    (slot === 'fx2' ? this.fx2 : this.fx1).setParam(name, value);
  }

  /** FX Settings window — Master EQ/Compressor on/off (see _createMasterInsert). `which` is 'eq' or 'comp'. */
  setMasterInsertEnabled(which, value) {
    (which === 'comp' ? this.masterComp : this.masterEq).setEnabled(value);
  }

  /** FX Settings window — live-tweak one Master EQ/Compressor param, no graph rebuild. `which` is 'eq' or 'comp'. */
  setMasterInsertParam(which, name, value) {
    (which === 'comp' ? this.masterComp : this.masterEq).setParam(name, value);
  }

  /** Session-boundary reset (see overrides.ts resetAll) — both master inserts back to disabled/default. */
  resetMasterInserts() {
    this.masterEq.reset(MASTER_EQ_DEFAULTS);
    this.masterComp.reset(MASTER_COMP_DEFAULTS);
  }

  /**
   * Drop all decoded samples and choke-group bookkeeping — call before
   * loading a new project. Does NOT stop currently-scheduled playback;
   * that's tracked by each playSequence() controller's own stop(), which
   * the caller is responsible for calling first (see useProjectStore).
   */
  reset() {
    this.buffers.clear();
    this.sampleTrims.clear();
    this.sampleChopTrims.clear();
    this.voices.clear();
    this.decodeWarnings = [];
    this._onsetBoundaryCache.clear();
    this._reversedBuffers = new WeakMap();
  }

  async loadSample(name, arrayBuffer, options = {}) {
    const shouldKeep = typeof options.shouldKeep === 'function' ? options.shouldKeep : () => true;
    if (!shouldKeep()) return;
    const key = AudioEngine.keyFor(name);
    const samplerInfo = readWavSamplerInfo(arrayBuffer);
    try {
      const buffer = await this.ctx.decodeAudioData(arrayBuffer.slice(0));
      if (!shouldKeep()) return;
      if (samplerInfo?.trim) this.sampleTrims.set(key, samplerInfo.trim);
      if (samplerInfo?.chopTrims?.some(Boolean)) this.sampleChopTrims.set(key, samplerInfo.chopTrims);
      this.buffers.set(key, buffer);
    } catch (err) {
      if (!shouldKeep()) return;
      console.warn(`Failed to decode ${name}:`, err);
      this.decodeWarnings.push({ name, error: String(err?.message ?? err) });
    }
  }

  getBuffer(sampleName) {
    return this.buffers.get(AudioEngine.keyFor(sampleName));
  }

  _reversedBuffer(buffer) {
    if (!buffer) return buffer;
    const cached = this._reversedBuffers.get(buffer);
    if (cached) return cached;
    const reversed = this.ctx.createBuffer(buffer.numberOfChannels, buffer.length, buffer.sampleRate);
    for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
      const input = buffer.getChannelData(ch);
      const output = reversed.getChannelData(ch);
      for (let i = 0, j = input.length - 1; i < input.length; i++, j--) {
        output[i] = input[j];
      }
    }
    this._reversedBuffers.set(buffer, reversed);
    return reversed;
  }

  _reverseTrim(trim, buffer) {
    if (!trim || !buffer) return trim;
    const start = Math.max(0, Math.min(trim.offsetSeconds, buffer.duration));
    const duration = Math.max(0, Math.min(trim.durationSeconds, buffer.duration - start));
    const offsetSeconds = Math.max(0, buffer.duration - start - duration);
    return { offsetSeconds, durationSeconds: duration };
  }

  /** A sample's on-disk Start/End trim, if any was set on the hardware — see readWavTrim. */
  getTrim(sampleName) {
    return this.sampleTrims.get(AudioEngine.keyFor(sampleName)) ?? null;
  }

  /** A stored OS2XL/OS3 Non-Destructive Chop window for one pad's chop number, if the WAV carries a multi-loop smpl table. */
  getSampleChopTrim(sampleName, chopIndex) {
    if (!Number.isInteger(chopIndex) || chopIndex < 0) return null;
    const trims = this.sampleChopTrims.get(AudioEngine.keyFor(sampleName));
    return trims?.[chopIndex] ?? null;
  }

  static storedChopIndexForPad(pad) {
    return Number.isInteger(pad?.storedChopIndex) && pad.storedChopIndex >= 0 ? pad.storedChopIndex : pad?.chopIndex;
  }

  getSampleChopTrimCount(sampleName) {
    return this.sampleChopTrims.get(AudioEngine.keyFor(sampleName))?.length ?? 0;
  }

  /**
   * `mode`: 'equal' (default, the file's own chopIndex/chopTotalSlices
   * divided evenly across the buffer) or 'onset' (see
   * _onsetChopBoundaries). Stored multi-loop WAV chop windows are handled
   * before this fallback in _padTrim; this method is only for inferred
   * chop families where the WAV did not expose explicit per-chop regions.
   */
  _chopTrim(pad, buffer, mode = 'equal') {
    if (!pad || !buffer || !pad.chopTotalSlices || pad.chopTotalSlices <= 1) return null;
    if (mode === 'onset') {
      const onsetTrim = this._onsetChopTrim(pad, buffer);
      if (onsetTrim) return onsetTrim;
      // Falls through to equal division if there aren't enough detected
      // transients to make an onset-based split meaningful.
    }
    const sliceDuration = buffer.duration / pad.chopTotalSlices;
    return { offsetSeconds: pad.chopIndex * sliceDuration, durationSeconds: sliceDuration };
  }

  _padTrim(sample, pad, buffer, mode = 'equal') {
    if (mode === 'full') return this.getTrim(sample.name);
    const storedIndex = mode === 'pad' ? pad?.chopIndex : AudioEngine.storedChopIndexForPad(pad);
    const storedChopTrim = pad?.chopTotalSlices > 1
      ? this.getSampleChopTrim(sample.name, storedIndex) ?? this.getSampleChopTrim(sample.name, pad?.chopIndex)
      : null;
    if (mode !== 'onset' && storedChopTrim) return storedChopTrim;
    return (
      this._chopTrim(pad, buffer, mode) ??
      storedChopTrim ??
      this.getTrim(sample.name)
    );
  }

  /**
   * `sliceCount` real transients, picked roughly evenly spaced through the
   * sample's own detected onset list (by index into that list, not by
   * time) — a stand-in for "the user dragged N markers to N of the real
   * hit starts" when the file's own equal-division data doesn't actually
   * match where the audio's hits are (see FORMAT.md/README's SEP 23
   * Rhodes/ISLEY DRUMS writeup). Cached per (sample, sliceCount): the scan
   * itself is cheap, but there's no reason to repeat it on every note.
   */
  _onsetChopTrim(pad, buffer) {
    const boundaries = this._onsetChopBoundaries(pad.samples?.[0]?.name, buffer, pad.chopTotalSlices);
    if (!boundaries) return null;
    const start = boundaries[pad.chopIndex];
    const end = pad.chopIndex + 1 < boundaries.length ? boundaries[pad.chopIndex + 1] : buffer.duration;
    if (start == null) return null;
    return { offsetSeconds: start, durationSeconds: Math.max(0.02, end - start) };
  }

  _onsetChopBoundaries(sampleName, buffer, sliceCount) {
    const cacheKey = `${sampleName ?? ''}::${sliceCount}`;
    if (this._onsetBoundaryCache.has(cacheKey)) return this._onsetBoundaryCache.get(cacheKey);

    const data = buffer.getChannelData(0);
    const sampleRate = buffer.sampleRate;
    const windowFrames = Math.max(1, Math.round(sampleRate * 0.002)); // 2ms
    const onsets = [];
    let wasQuiet = true;
    for (let start = 0; start < data.length; start += windowFrames) {
      let peak = 0;
      const end = Math.min(start + windowFrames, data.length);
      for (let i = start; i < end; i++) {
        const v = Math.abs(data[i]);
        if (v > peak) peak = v;
      }
      if (peak > 0.15 && wasQuiet) onsets.push(start / sampleRate);
      wasQuiet = peak < 0.05;
    }

    // Not enough real transients to make an onset-based split meaningful —
    // cache the "give up" result too, so a sparse/quiet sample doesn't get
    // re-scanned on every note.
    let boundaries = null;
    if (onsets.length >= sliceCount) {
      boundaries = [];
      for (let i = 0; i < sliceCount; i++) {
        boundaries.push(onsets[Math.floor((i * onsets.length) / sliceCount)]);
      }
    }
    this._onsetBoundaryCache.set(cacheKey, boundaries);
    return boundaries;
  }

  /**
   * True if `track` is a genuine linear audio-take layer, not a MIDI/drum
   * track that merely happens to be named after a loaded sample — see
   * resolveAudioTracks' own comment for the real-project evidence behind
   * requiring typeCode === 5 alongside the name/buffer match, and (when a
   * `sequence` is given) why a track with a substantial real note pattern
   * doesn't count as an audio layer even if typeCode says 5. Shared here
   * so the UI (which track detail view to show, which tracks count as
   * "active") uses the exact same rule as actual playback, rather than
   * each place re-deriving its own (previously buggy) copy.
   */
  isAudioTrack(track, sequence) {
    if (!track?.name || track.typeCode !== 5 || !this.getBuffer(track.name)) return false;
    if (sequence) {
      let noteCount = 0;
      for (const ev of sequence.events) {
        if (ev.status === NOTE_ON_STATUS && ev.track === track.number && ++noteCount > 5) return false;
      }
    }
    return true;
  }

  /**
   * Same verdict as isAudioTrack, but with the *reasoning* attached —
   * powers the Debug panel so a track's classification is inspectable
   * instead of a black box. Never used on the playback hot path (isAudioTrack
   * stays the fast, reason-free version actual scheduling calls).
   */
  classifyTrack(track, sequence) {
    if (!track?.name) return { isAudioTrack: false, reason: 'no name' };
    const hasBuffer = !!this.getBuffer(track.name);
    let noteCount = 0;
    if (sequence) {
      for (const ev of sequence.events) {
        if (ev.status === NOTE_ON_STATUS && ev.track === track.number) noteCount++;
      }
    }
    if (track.typeCode !== 5) {
      return { isAudioTrack: false, reason: `typeCode is ${track.typeCode}, not 5`, hasBuffer, noteCount };
    }
    if (!hasBuffer) {
      return { isAudioTrack: false, reason: 'typeCode is 5 but no WAV with this exact name is loaded', hasBuffer, noteCount };
    }
    if (noteCount > 5) {
      return {
        isAudioTrack: false,
        reason: `typeCode is 5 but has ${noteCount} real note events — treated as a MIDI/drum track instead`,
        hasBuffer,
        noteCount,
      };
    }
    return { isAudioTrack: true, reason: 'typeCode 5, matching WAV loaded, no substantial note content', hasBuffer, noteCount };
  }

  /**
   * What kind of track this is, for display only (never gates playback —
   * scheduling already treats any non-audio track with a resolvable
   * program uniformly, which is correct per the stock MPC1000 manual:
   * DRUM vs. MIDI track *type* barely matters for audio output, what
   * actually matters is whether a Program is assigned or left OFF for
   * pure external MIDI-out). Surveyed the real library's typeCode
   * distribution (~19.7K tracks across 131 projects) rather than guess:
   * 5 is confirmed Audio; nearly all other values (~98%, typeCode 1/2 in
   * the survey) carry a real assigned program and heavy note content —
   * these get labeled "Pads" rather than a specific JJOS type name
   * (Drum/MIDI/Chord), since typeCode 1 vs. 2 show near-identical
   * profiles and there's no confirmed mapping to tell them apart. A small
   * minority (typeCode 3 in the survey — 13 tracks total across 131 real
   * projects) skew heavily toward *no* program assigned at all, matching
   * the manual's description of a MIDI track wired to external gear
   * (Pgm=OFF) — labeled "External" since there's no local sound for this
   * player to produce or a mute/volume control to meaningfully affect
   * without real MIDI-out hardware support.
   */
  trackKind(track) {
    if (!track) return 'unused';
    if (track.typeCode === 5) return 'audio';
    const hasProgram = !!track.programName?.trim();
    return hasProgram ? 'pads' : 'external';
  }

  /** Resolve one pad's sample for a given velocity (falls back to the first slot). */
  static pickSample(pad, velocity) {
    if (!pad || pad.samples.length === 0) return null;
    return (
      pad.samples.find((s) => velocity >= s.rangeLower && velocity <= s.rangeUpper) ||
      pad.samples[0]
    );
  }

  /**
   * Create a persistent per-track bus (gain + pan), so mute/level/pan
   * changes made *during* playback — via the returned handle — are
   * instant, affecting already-scheduled and future notes alike, rather
   * than waiting for the next loop iteration.
   */
  _createBus() {
    const ctx = this.ctx;
    const gainNode = ctx.createGain();
    const pannerNode = ctx.createStereoPanner();
    gainNode.connect(pannerNode).connect(this.master);
    // Passive tap for the Trk Mixer's per-track activity LEDs (see
    // getLevel below) — a parallel fan-out from pannerNode, not part of
    // the signal path to master, so it can't affect what's actually
    // audible. Reads real post-fader/post-mute signal, not just a gain
    // value, so a track sitting at full level with nothing playing
    // correctly reads as silent.
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0;
    pannerNode.connect(analyser);
    const analyserBuf = new Float32Array(analyser.fftSize);
    // Chromium (and likely other engines) prunes a node's automation
    // processing once no active source is feeding it — a track bus that
    // goes quiet between hits stops honoring any further setTargetAtTime
    // call (a live fader drag, a mute toggle, CC#7/10 automation) until
    // the next note plays, silently. A permanently-running zero-amplitude
    // ConstantSourceNode keeps this subgraph in the active render set
    // continuously, at zero audible cost, so live edits always land.
    const keepAlive = ctx.createConstantSource();
    keepAlive.offset.value = 0;
    keepAlive.connect(gainNode);
    keepAlive.start();
    // Post-fader send tap into the shared FX bus (see the constructor) —
    // starts at zero and stays there unless setSend is actually called,
    // so a bus is silent into the reverb until FX sends are enabled.
    const sendGainNode = ctx.createGain();
    sendGainNode.gain.value = 0;
    gainNode.connect(sendGainNode).connect(this.fxSendBus);
    let lastLevel = 1;
    let muted = false;
    return {
      gainNode,
      pannerNode,
      input: gainNode,
      // Track Mixer Level is a MIDI CC#7-range value (0-127, not 0-100) —
      // confirmed against real hardware (a track set to 127 on the MPC1000
      // itself) and consistent with the CC#7 automation this same setter
      // receives live (see scheduleBeat's CC#7/10/91 handling). TrkMixer.vue
      // already treats it as 0-127 for the fader display; this was the one
      // remaining place still dividing by 100.
      setLevel(level0to127) {
        lastLevel = Math.max(0, (level0to127 ?? 110) / 127);
        if (!muted) gainNode.gain.setTargetAtTime(lastLevel, ctx.currentTime, 0.01);
      },
      setMuted(isMuted) {
        muted = isMuted;
        gainNode.gain.setTargetAtTime(muted ? 0 : lastLevel, ctx.currentTime, 0.01);
      },
      setPan(pan0to100) {
        pannerNode.pan.setTargetAtTime(panFrom0to100(pan0to100), ctx.currentTime, 0.01);
      },
      setSend(send0to100) {
        sendGainNode.gain.setTargetAtTime(Math.max(0, (send0to100 ?? 0) / 100), ctx.currentTime, 0.01);
      },
      /** Current peak amplitude (0-1) actually leaving this track's bus right now - for the Trk Mixer's activity LEDs, not audio-accurate metering. */
      getLevel() {
        analyser.getFloatTimeDomainData(analyserBuf);
        let peak = 0;
        for (let i = 0; i < analyserBuf.length; i++) {
          const abs = Math.abs(analyserBuf[i]);
          if (abs > peak) peak = abs;
        }
        return Math.min(1, peak);
      },
      dispose() {
        try {
          keepAlive.stop();
          keepAlive.disconnect();
          analyser.disconnect();
          gainNode.disconnect();
          pannerNode.disconnect();
          sendGainNode.disconnect();
        } catch {
          /* already disconnected */
        }
      },
    };
  }

  /** Play a single pad immediately, for pad-grid preview clicks. */
  previewPad(pad, programName, velocity = 100, overrides = {}) {
    const ov = { ...DEFAULT_OVERRIDES, ...overrides };
    const sample = AudioEngine.pickSample(pad, velocity);
    if (!sample) return;
    const buffer = this.getBuffer(sample.name);
    if (!buffer) return;
    const gain =
      Math.max(0, ((sample.level ?? 100) / 100) * padLevelGain(ov.getPadLevel(programName, pad))) *
      velocityModulatedGain(pad?.velocityToLevel, velocity);
    const chokeKeys = [];
    if (pad?.voiceOverlap === 1) chokeKeys.push(`${programName}::pad::${pad.index}`);
    if (pad?.muteGroup > 0) chokeKeys.push(`${programName}::group::${pad.muteGroup}`);
    const tuning = (sample.tuning || 0) + ov.getPadTuningOffset(programName, pad);
    const reverse = ov.getPadReverse(programName, pad);
    this._playOne(
      buffer,
      {
        gain,
        pan: panFrom0to100(ov.getPadPan(programName, pad)),
        playbackRate: Math.pow(2, tuning / 1200),
        reverse,
        chokeKeys,
        attack: ov.getPadAttack(programName, pad),
        decay: ov.getPadDecay(programName, pad),
        decayMode: pad?.decayMode,
        sustain: ov.getPadSustain(programName, pad),
        release: ov.getPadRelease(programName, pad),
        filters: this._padFilters(pad, undefined, undefined, velocity),
        filterInputGain: filterAttenuationGain(pad?.filterAttenuation),
        trim: this._padTrim(sample, pad, buffer, ov.getChopMode(programName, pad)),
      },
      this.ctx.currentTime
    );
  }

  /**
   * Play a raw buffer straight through at unity gain/pan, once — for
   * "what does this file actually sound like" preview clicks (an audio-
   * track's full take, or any other loaded sample) that aren't going
   * through a pad at all. Always uses the same choke key regardless of
   * which file — previewing anything new stops whatever preview (if any)
   * was already playing, rather than layering unrelated files on top of
   * each other. Returns the source node so a caller can track when it
   * naturally ends (`src.onended`).
   */
  previewBuffer(buffer) {
    if (!buffer) return null;
    return this._playOne(buffer, { gain: 1, chokeKeys: [AudioEngine.PREVIEW_CHOKE_KEY] }, this.ctx.currentTime);
  }

  /** Stop whatever's currently playing via previewBuffer, if anything. */
  stopPreview() {
    this._chokeVoice(AudioEngine.PREVIEW_CHOKE_KEY, this.ctx.currentTime);
  }

  /**
   * `freqOverride`/`resOverride` (0-100 scale, same domain as the pad's own
   * static filter1Freq/filter1Res) let recorded Q-Link CC automation
   * (CC#74/71 — see resolveEvents' automation-lane comment) drive filter 1
   * per-note instead of the pad's fixed value. Filter 2 is never automated
   * this way — no evidence in any sampled project of Q-Link CC data meant
   * for a second filter slot.
   */
  _filterSpec(type, freq, res, velToFreq, velocity) {
    const biquadType = FILTER_TYPES[type];
    if (!biquadType) return null;
    return {
      type: biquadType,
      freq: velocityModulatedFilterFreq(freq, velToFreq, velocity),
      res: clamp0to100(res, 0),
    };
  }

  _padFilters(pad, freqOverride, resOverride, velocity = 127) {
    if (!pad) return [];
    const filters = [];
    const filter1 = this._filterSpec(
      pad.filter1Type,
      freqOverride ?? pad.filter1Freq,
      resOverride ?? pad.filter1Res,
      pad.filter1VelToFreq,
      velocity
    );
    if (filter1) filters.push(filter1);
    // Filter 2 type 4 is "Link": hardware applies filter 1's settings again,
    // producing a steeper two-filter sound while ignoring filter 2 freq/res.
    if (pad.filter2Type === 4) {
      if (filter1) filters.push({ ...filter1 });
    } else {
      const filter2 = this._filterSpec(pad.filter2Type, pad.filter2Freq, pad.filter2Res, pad.filter2VelToFreq, velocity);
      if (filter2) filters.push(filter2);
    }
    return filters;
  }

  /** Fade out and stop whatever's currently registered under `key`, at `when`. */
  _chokeVoice(key, when) {
    const existing = this.voices.get(key);
    if (!existing) return;
    const { src, gainNode } = existing;
    try {
      gainNode.gain.cancelScheduledValues(when);
      gainNode.gain.setValueAtTime(gainNode.gain.value, when);
      gainNode.gain.linearRampToValueAtTime(0, when + CHOKE_FADE_SECONDS);
      src.stop(when + CHOKE_FADE_SECONDS + 0.005);
    } catch {
      /* already stopped */
    }
    if (this.voices.get(key)?.src === src) this.voices.delete(key);
  }

  /**
   * Play one buffer with the given gain/pan/rate, at `when`, into an
   * optional destination bus (defaults to master). `attack`/mode-1 `decay`/
   * `sustain`/`release` (PGM's 0-100 envelope knobs — see FORMAT.md and
   * decaySeconds/releaseSeconds) shape the amplitude as a real ADSR: Attack
   * ramps 0->peak, mode-1 Decay ramps peak->sustain level, the note holds
   * at sustain until it ends, then Release ramps sustain->0. `filters`
   * (from _padFilters) are inserted before the gain stage. `chokeKeys`
   * (mute group / mono voice / etc.) cut off whatever previously
   * registered under the same key(s). `stopAt`, if given, gates playback
   * at that time (for duration-bound "Note On" mode samples) — Release
   * then shapes the fade-out starting there. Without `stopAt` (One Shot,
   * the common default play mode), Release is not applied — see the
   * comment further down for why that was tried and reverted.
   */
  _playOne(
    buffer,
    { gain, pan = 0, playbackRate = 1, reverse = false, chokeKeys = [], attack, decay, decayMode = 0, sustain, release, filters = [], filterInputGain = 1, trim = null },
    when,
    stopAt = null,
    destination = null
  ) {
    // A caller-computed `when` can land slightly (or badly) in the past —
    // e.g. a scheduler catching up after falling behind real time (see
    // scheduleBeat's catch-up guard). Every Web Audio scheduling call below
    // requires a non-negative time or throws (AudioParam.setValueAtTime,
    // AudioBufferSourceNode.stop), which would otherwise crash playback
    // outright instead of just starting a hair late. Clamp once here so
    // every call site is protected, not just ones that remember to.
    when = Math.max(when, this.ctx.currentTime);
    if (stopAt != null) stopAt = Math.max(stopAt, when);
    for (const key of chokeKeys) this._chokeVoice(key, when);

    const sourceBuffer = reverse ? this._reversedBuffer(buffer) : buffer;
    const sourceTrim = reverse ? this._reverseTrim(trim, buffer) : trim;
    const src = this.ctx.createBufferSource();
    src.buffer = sourceBuffer;
    src.playbackRate.value = playbackRate;

    let node = src;
    if (filters.length && filterInputGain !== 1) {
      const filterInput = this.ctx.createGain();
      filterInput.gain.value = filterInputGain;
      node.connect(filterInput);
      node = filterInput;
    }
    for (const f of filters) {
      const biquad = this.ctx.createBiquadFilter();
      biquad.type = f.type;
      biquad.frequency.value = filterFreqHz(f.freq);
      biquad.Q.value = filterQ(f.res);
      node.connect(biquad);
      node = biquad;
    }

    const gainNode = this.ctx.createGain();
    const target = Math.max(0, gain);
    const fadeIn = Math.max(MIN_FADE_SECONDS, attackSeconds(attack));
    gainNode.gain.setValueAtTime(0, when);
    gainNode.gain.linearRampToValueAtTime(target, when + fadeIn);

    const sustainLevel = target * (clamp0to100(sustain, 100) / 100);
    const decayTime = decaySeconds(decay, decayMode);
    let sustainStart = when + fadeIn;
    if (decayTime > 0) {
      gainNode.gain.setValueAtTime(target, when + fadeIn);
      gainNode.gain.linearRampToValueAtTime(sustainLevel, when + fadeIn + decayTime);
      sustainStart = when + fadeIn + decayTime;
    }

    const panner = this.ctx.createStereoPanner();
    panner.pan.value = pan;

    node.connect(gainNode).connect(panner).connect(destination ?? this.master);
    if (sourceTrim) {
      src.start(when, sourceTrim.offsetSeconds, sourceTrim.durationSeconds);
    } else {
      src.start(when);
    }

    const relTime = releaseSeconds(release);
    const bufferSeconds = (sourceTrim ? sourceTrim.durationSeconds : buffer.duration) / (playbackRate || 1);
    const naturalBufferEnd = when + bufferSeconds;
    if (stopAt != null) {
      // Gated ("Note On"/Gate mode). A real Release value extends the
      // fade-out *past* the recorded gate length — like a synth envelope
      // releasing after note-off — rather than compressing into it.
      // Clamped to the buffer's own natural end since there's no more
      // audio to shape past that regardless. No Release configured falls
      // back to the original fast declick-only fade ending exactly at the
      // gate time (unchanged behavior).
      if (relTime > 0) {
        const releaseEnd = Math.min(stopAt + relTime, naturalBufferEnd);
        const rampStart = Math.max(sustainStart, stopAt);
        try {
          gainNode.gain.cancelScheduledValues(rampStart);
          gainNode.gain.setValueAtTime(gainNode.gain.value, rampStart);
          gainNode.gain.linearRampToValueAtTime(0, releaseEnd);
          src.stop(releaseEnd + 0.005);
        } catch {
          /* already scheduled to stop naturally */
        }
      } else {
        const rampStart = Math.max(sustainStart, stopAt - CHOKE_FADE_SECONDS);
        try {
          gainNode.gain.cancelScheduledValues(rampStart);
          gainNode.gain.setValueAtTime(gainNode.gain.value, rampStart);
          gainNode.gain.linearRampToValueAtTime(0, stopAt);
          src.stop(stopAt + 0.005);
        } catch {
          /* already scheduled to stop naturally */
        }
      }
    }
    // One Shot (no stopAt) is deliberately left alone regardless of
    // Release: an earlier version also shaped a Release fade ending at
    // the sample's natural end for One Shot pads, but that applies to
    // *every* normal hit of a pad (One Shot is the common default play
    // mode, not just the Note-On/Gate case Release is meant to react to)
    // and audibly blurred fast-retriggered patterns that used to sound
    // clean — confirmed against dr55-sd11's real Note Repeat pattern.
    // Release now only shapes the tail after an explicit gate-off.

    for (const key of chokeKeys) this.voices.set(key, { src, gainNode });
    return src;
  }

  /**
   * Every per-note gain/pan/tuning/trim/choke computation a resolved note
   * event needs for `_playOne` — pulled out into its own method (rather
   * than living only inside scheduleBeat's loop) so the stems export
   * (renderTrackOffline) computes a note's sound exactly the same way live
   * playback does, from the same overrides resolver, instead of
   * maintaining a second copy that could quietly drift out of sync with
   * what you actually hear.
   */
  _notePlaybackParams(re, ov, when) {
    const padLevel = ov.getPadLevel(re.track.programName, re.pad);
    const gain =
      Math.max(0, (re.sample.level ?? 100) / 100) *
      padLevelGain(padLevel) *
      velocityModulatedGain(re.pad?.velocityToLevel, re.velocity);
    const tuning = (re.sample.tuning || 0) + ov.getPadTuningOffset(re.track.programName, re.pad) + re.pitchBendCents;
    const effectivePlayMode = ov.getPadPlayModeOverride(re.track.programName, re.pad) ?? re.sample.playMode;
    const stopAt =
      effectivePlayMode === 1 && re.sample._durationSeconds
        ? when + Math.max(re.sample._durationSeconds, 0.02)
        : null;
    return {
      gain,
      pan: panFrom0to100(ov.getPadPan(re.track.programName, re.pad)),
      tuningRate: Math.pow(2, tuning / 1200),
      reverse: ov.getPadReverse?.(re.track.programName, re.pad) ?? !!re.pad?.reverse,
      stopAt,
      attack: re.ccAttack ?? ov.getPadAttack(re.track.programName, re.pad),
      decay: re.ccDecay ?? ov.getPadDecay(re.track.programName, re.pad),
      decayMode: re.pad?.decayMode,
      sustain: ov.getPadSustain(re.track.programName, re.pad),
      release: ov.getPadRelease(re.track.programName, re.pad),
      filters: this._padFilters(re.pad, re.ccFilterFreq, re.ccFilterRes, re.velocity),
      filterInputGain: filterAttenuationGain(re.pad?.filterAttenuation),
      trim: this._padTrim(re.sample, re.pad, re.buffer, ov.getChopMode(re.track.programName, re.pad)),
      chokeKeys: [
        re.pad?.voiceOverlap === 1 ? `${re.track.programName}::pad::${re.pad.index}` : null,
        re.pad?.muteGroup > 0 ? `${re.track.programName}::group::${re.pad.muteGroup}` : null,
      ].filter(Boolean),
    };
  }

  /**
   * Resolve a sequence's events against the project's programs, returning
   * playable event objects with everything the scheduler + UI need. Only
   * real Note On (0x90) records become audible hits — see NOTE_ON_STATUS.
   * Mute/solo is *not* applied here — every track's notes are always
   * scheduled; audibility is gated live via each track's persistent bus
   * (see playSequence), so toggling mute mid-playback is instant instead
   * of waiting for the next loop. programsByName: Map<programName, ParsedPGM>
   */
  resolveEvents(
    sequence,
    programsByName,
    secondsPerTick = 60 / sequence.bpm / sequence.ticksPerBeat,
    automationLanes = buildAutomationLanes(sequence.events),
    events = sequence.events
  ) {
    const { pitchBendByTrack, ccByTrack } = automationLanes;
    const resolved = [];
    for (const ev of events) {
      if (ev.status !== NOTE_ON_STATUS) continue;
      const track = sequence.tracks[ev.track - 1];
      if (!track) continue;
      const program = programsByName.get(track.programName);
      if (!program) continue;
      const resolvedNote = program.resolveNote?.(ev.note) ?? { pad: program.padForNote?.(ev.note) ?? null, fallback: null };
      const pad = resolvedNote.pad;
      const sample = AudioEngine.pickSample(pad, ev.velocity);
      if (!sample) continue;
      const buffer = this.getBuffer(sample.name);
      if (!buffer) continue;
      // Recorded pitch-bend/Q-Link CC automation, sampled at this note's
      // own tick (step/hold — see lookupAtOrBefore) and baked in once at
      // resolve time, same as everything else here: resolveEvents runs
      // fresh every beat (see scheduleBeat), so a note a few beats out
      // still gets whatever automation value is current when it's
      // actually resolved, not a stale snapshot from playback start.
      const pitchBendCents = lookupAtOrBefore(pitchBendByTrack.get(ev.track), ev.tick)?.value ?? 0;
      const ccLanes = ccByTrack.get(ev.track);
      // CC values are 0-127; pad.filter1Freq/Res/attack/decay are 0-100 —
      // scale into that same domain so the override composes cleanly with
      // the curve-mapping functions built for it.
      const ccScale = 100 / 127;
      const ccFilterFreq = lookupAtOrBefore(ccLanes?.get(CC_FILTER_FREQ), ev.tick)?.value;
      const ccFilterRes = lookupAtOrBefore(ccLanes?.get(CC_FILTER_RES), ev.tick)?.value;
      const ccAttack = lookupAtOrBefore(ccLanes?.get(CC_ATTACK), ev.tick)?.value;
      const ccDecay = lookupAtOrBefore(ccLanes?.get(CC_DECAY), ev.tick)?.value;
      // Note: level/pan/tuning overrides are deliberately NOT baked in here —
      // resolveEvents runs once per beat (see scheduleBeat), so reading
      // them fresh each time lets pad-mixer edits made mid-playback apply
      // within a fraction of a second, without needing a stop/restart.
      resolved.push({
        ...ev,
        track,
        pad,
        sample: { ...sample, _durationSeconds: ev.duration * secondsPerTick },
        buffer,
        noteFallback: resolvedNote.fallback,
        offsetSeconds: ev.tick * secondsPerTick,
        pitchBendCents,
        ccFilterFreq: ccFilterFreq != null ? ccFilterFreq * ccScale : undefined,
        ccFilterRes: ccFilterRes != null ? ccFilterRes * ccScale : undefined,
        ccAttack: ccAttack != null ? ccAttack * ccScale : undefined,
        ccDecay: ccDecay != null ? ccDecay * ccScale : undefined,
      });
    }
    return resolved;
  }

  /**
   * Detect audio tracks: a track whose name matches a loaded sample buffer
   * is a full-length audio recording layered under the sequence (JJOS's
   * "record audio over your MIDI drums" workflow), not a MIDI/drum track —
   * see FORMAT.md. Mute/solo handled live via the track's bus, same as
   * resolveEvents.
   *
   * Name-matching alone isn't enough: a normal MIDI/drum track can be
   * renamed to match one of its own program's pad samples (confirmed on a
   * real project, `July 25`: tracks 2/4 renamed to `"Sample02"`, matching
   * a Program03 pad sample, while remaining genuine note-triggered
   * tracks) — treating that as an audio layer replaces its real note
   * pattern with the wrong, naive "play the whole sample once per loop."
   * `typeCode` (track record offset 0x23) disambiguates: confirmed 5 on
   * every genuine audio track sampled across three real projects
   * (including one also named `"Sample01"`, ruling out name-prefix alone
   * as the signal), vs. 2 on July 25's false-positive tracks — matches
   * FORMAT.md's own earlier statistical survey (5 correlates 88% with
   * audio tracks) that this detection never actually used until now.
   */
  resolveAudioTracks(sequence) {
    const layers = [];
    for (const track of sequence.tracks) {
      if (!this.isAudioTrack(track, sequence)) continue;
      layers.push({ track, buffer: this.getBuffer(track.name) });
    }
    return layers;
  }

  /**
   * Render one track's own dry signal for the sequence's own loop length
   * (stems export — see StemsExport.vue), respecting whatever level/pan/
   * mute/solo/tuning/Play-Mode/chop/varispeed overrides are live right now
   * via the same resolver `playSequence` uses, so an export matches what
   * you'd currently hear — including the Pitch Control knob: turning it
   * before exporting scales tempo and pitch together in the bounce exactly
   * like it does live. Deliberately dry, though: no shared limiter or FX
   * reverb send baked in (those are mix-bus effects on the *combined*
   * signal, not a per-track characteristic — printing them per-stem would
   * double them up once re-combined in a DAW). Returns `null` if the track
   * has nothing to render (muted/soloed-out, no notes, no matching audio-
   * layer buffer) rather than a silent buffer, so the caller can skip it
   * entirely.
   */
  async renderTrackOffline(sequence, programsByName, trackNumber, overrides = {}) {
    const ov = { ...DEFAULT_OVERRIDES, ...overrides };
    const track = sequence.tracks[trackNumber - 1];
    if (!track) return null;
    if (!ov.isTrackAudible(trackNumber, track)) return null;

    const varispeed = Math.max(0.5, Math.min(1.5, ov.getVarispeed() || 1));
    const secondsPerTick = 60 / sequence.bpm / sequence.ticksPerBeat / varispeed;
    const loopLengthSeconds = sequence.bars * sequence.ticksPerBar * secondsPerTick;
    // A little tail past the loop boundary so a note/gate/decay near the
    // very end of the loop isn't cut off mid-fade.
    const tailSeconds = 1.5;
    const sampleRate = this.ctx.sampleRate;
    const offlineCtx = new OfflineAudioContext(2, Math.ceil((loopLengthSeconds + tailSeconds) * sampleRate), sampleRate);

    // Borrows AudioEngine's prototype methods (_playOne, resolveEvents,
    // _padFilters, _padTrim, _chopTrim, getBuffer, getTrim,
    // getSampleChopTrim, isAudioTrack,
    // _chokeVoice) onto a lightweight object pointed at the offline
    // context instead — same exact logic as live playback, no separate
    // implementation to keep in sync, without paying for a second real
    // AudioContext/reverb/limiter graph per track rendered.
    const bounce = Object.create(AudioEngine.prototype);
    bounce.ctx = offlineCtx;
    bounce.buffers = this.buffers;
    bounce.sampleTrims = this.sampleTrims;
    bounce.sampleChopTrims = this.sampleChopTrims;
    bounce._reversedBuffers = new WeakMap();
    bounce.voices = new Map();

    const bus = offlineCtx.createGain();
    const panner = offlineCtx.createStereoPanner();
    bus.connect(panner).connect(offlineCtx.destination);
    bus.gain.value = Math.max(0, (ov.getTrackLevel(trackNumber, track) ?? 110) / 127);
    panner.pan.value = panFrom0to100(ov.getTrackPan(trackNumber, track));
    bounce.master = bus;

    let scheduledAnything = false;

    if (bounce.isAudioTrack(track, sequence)) {
      const buffer = bounce.getBuffer(track.name);
      if (buffer) {
        bounce._playOne(buffer, { gain: 1, playbackRate: varispeed }, 0, null, bus);
        scheduledAnything = true;
      }
    } else {
      const automationLanes = buildAutomationLanes(sequence.events);
      const resolvedEvents = bounce.resolveEvents(sequence, programsByName, secondsPerTick, automationLanes);
      for (const re of resolvedEvents) {
        if (re.track.number !== trackNumber) continue;
        const when = re.offsetSeconds;
        const p = bounce._notePlaybackParams(re, ov, when);
        bounce._playOne(
          re.buffer,
          {
            gain: p.gain,
            pan: p.pan,
            playbackRate: p.tuningRate * varispeed,
            reverse: p.reverse,
            attack: p.attack,
            decay: p.decay,
            decayMode: p.decayMode,
            filters: p.filters,
            filterInputGain: p.filterInputGain,
            trim: p.trim,
            chokeKeys: p.chokeKeys,
          },
          when,
          p.stopAt,
          bus
        );
        scheduledAnything = true;
      }
    }

    if (!scheduledAnything) return null;
    return offlineCtx.startRendering();
  }

  /**
   * Render every audible track of `sequence` mixed together for one loop,
   * through the same limiter + shared FX-send/FX1/FX2 chain live playback
   * uses (see the constructor and `_createBus`/`playSequence`) rather than
   * renderTrackOffline's deliberately dry per-track bounce — this collapses
   * to one final signal, so there's no re-combination to double-print FX
   * into. Used by `renderSongOffline` as the per-step building block for a
   * whole-song mixdown (see SongExport.vue).
   */
  async renderSequenceOffline(sequence, programsByName, overrides = {}) {
    const ov = { ...DEFAULT_OVERRIDES, ...overrides };
    const varispeed = Math.max(0.5, Math.min(1.5, ov.getVarispeed() || 1));
    const secondsPerTick = 60 / sequence.bpm / sequence.ticksPerBeat / varispeed;
    const loopLengthSeconds = sequence.bars * sequence.ticksPerBar * secondsPerTick;
    const tailSeconds = 1.5;
    const sampleRate = this.ctx.sampleRate;
    const offlineCtx = new OfflineAudioContext(2, Math.ceil((loopLengthSeconds + tailSeconds) * sampleRate), sampleRate);

    const bounce = Object.create(AudioEngine.prototype);
    bounce.ctx = offlineCtx;
    bounce.buffers = this.buffers;
    bounce.sampleTrims = this.sampleTrims;
    bounce.sampleChopTrims = this.sampleChopTrims;
    bounce._reversedBuffers = new WeakMap();
    bounce.voices = new Map();

    // Same master gain -> limiter -> destination and FX-send -> reverb ->
    // master shape as the constructor, rebuilt on this offline context
    // (AudioNodes can't cross contexts) so `_createBus` — otherwise
    // unmodified — produces a bus that mixes exactly like a live one.
    bounce.master = offlineCtx.createGain();
    bounce.master.gain.value = this.master.gain.value;
    // Same shared _wireMasterChain the constructor uses, so this can't
    // drift from live topology — but seeded with the *live* instance's
    // current EQ/Comp params (not the fresh-session defaults) so an
    // export starts from what's actually playing, then activated to
    // match whether they're actually on. `instant: true` — this is
    // initial setup on a graph that hasn't rendered anything yet, not a
    // live transition, and setEnabled's normal ramped path uses
    // setTimeout to defer the swap, which wouldn't land at the right
    // point in an OfflineAudioContext's non-real-time render anyway.
    bounce._wireMasterChain(offlineCtx.destination, {
      eqDefaults: this.masterEq.params,
      compDefaults: this.masterComp.params,
    });
    bounce.masterEq.setEnabled(this.masterEq.enabled, { instant: true });
    bounce.masterComp.setEnabled(this.masterComp.enabled, { instant: true });
    bounce.fxSendBus = offlineCtx.createGain();
    bounce.fx1 = bounce._createFxSlot(bounce.master);
    bounce.fx2 = bounce._createFxSlot(bounce.master);
    bounce.fxSendBus.connect(bounce.fx1.input);
    bounce.fxSendBus.connect(bounce.fx2.input);
    bounce.fx1.setType(this.fx1.type, this.fx1.params);
    bounce.fx2.setType(this.fx2.type, this.fx2.params);

    const trackBuses = new Map();
    const busFor = (trackNumber, track) => {
      let bus = trackBuses.get(trackNumber);
      if (!bus) {
        bus = bounce._createBus();
        bus.setLevel(ov.getTrackLevel(trackNumber, track));
        bus.setPan(ov.getTrackPan(trackNumber, track));
        if (ov.isFxEnabled()) bus.setSend(ov.getTrackSend(trackNumber, track));
        trackBuses.set(trackNumber, bus);
      }
      return bus;
    };

    let scheduledAnything = false;

    for (const layer of bounce.resolveAudioTracks(sequence)) {
      const { track, buffer } = layer;
      if (!buffer || !ov.isTrackAudible(track.number, track)) continue;
      const bus = busFor(track.number, track);
      bounce._playOne(buffer, { gain: 1, playbackRate: varispeed }, 0, null, bus.input);
      scheduledAnything = true;
    }

    const automationLanes = buildAutomationLanes(sequence.events);
    const resolvedEvents = bounce.resolveEvents(sequence, programsByName, secondsPerTick, automationLanes);
    for (const re of resolvedEvents) {
      // Audio tracks are handled above as one whole-buffer layer, same
      // either/or split renderTrackOffline uses per stem.
      if (bounce.isAudioTrack(re.track, sequence)) continue;
      if (!ov.isTrackAudible(re.track.number, re.track)) continue;
      const bus = busFor(re.track.number, re.track);
      const when = re.offsetSeconds;
      const p = bounce._notePlaybackParams(re, ov, when);
      bounce._playOne(
        re.buffer,
        {
          gain: p.gain,
          pan: p.pan,
          playbackRate: p.tuningRate * varispeed,
          reverse: p.reverse,
          attack: p.attack,
          decay: p.decay,
          decayMode: p.decayMode,
          filters: p.filters,
          filterInputGain: p.filterInputGain,
          trim: p.trim,
          chokeKeys: p.chokeKeys,
        },
        when,
        p.stopAt,
        bus.input
      );
      scheduledAnything = true;
    }

    if (!scheduledAnything) return null;
    return offlineCtx.startRendering();
  }

  /**
   * Bounce a whole song to one continuous mixdown. `entries` is a song's
   * steps already expanded through their repeats (see playback.ts's
   * `expandedSongEntries`) — each rendered via `renderSequenceOffline` in
   * its own step's tempo (a song can chain sequences with different BPMs;
   * see `buildSongSequence` in parsers/all.js for why the merged
   * `song.sequence` itself isn't tempo-accurate enough to render from),
   * then stitched back-to-back at exact loop boundaries — the same
   * `nextStartAt = loopLengthSeconds` math `playSong` schedules live, no
   * gap between steps. Each step's own tail (a decay or reverb ringing
   * past its loop boundary) is additively mixed into the following step's
   * audio rather than truncated, matching how it naturally keeps sounding
   * live while the next step's notes start on top of it.
   *
   * `resolveOverrides(sequence)` returns that step's own live overrides
   * (level/pan/mute/tuning/chop), same as `playSong` re-resolving per step
   * — a song can chain sequences that each carry different overrides.
   * `onProgress(done, total)` fires after each step renders.
   */
  async renderSongOffline(entries, programsByName, resolveOverrides, onProgress) {
    if (!entries.length) return null;
    const sampleRate = this.ctx.sampleRate;

    const loopLengths = entries.map(({ sequence }) => {
      const ov = { ...DEFAULT_OVERRIDES, ...resolveOverrides(sequence) };
      const varispeed = Math.max(0.5, Math.min(1.5, ov.getVarispeed() || 1));
      const secondsPerTick = 60 / sequence.bpm / sequence.ticksPerBeat / varispeed;
      return sequence.bars * sequence.ticksPerBar * secondsPerTick;
    });
    const offsetsSeconds = [];
    let cursor = 0;
    for (const len of loopLengths) {
      offsetsSeconds.push(cursor);
      cursor += len;
    }
    const finalTailSeconds = 1.5;
    const totalSamples = Math.ceil((cursor + finalTailSeconds) * sampleRate);
    const master = this.ctx.createBuffer(2, totalSamples, sampleRate);
    const masterChannels = [master.getChannelData(0), master.getChannelData(1)];

    for (let i = 0; i < entries.length; i++) {
      const sequence = entries[i].sequence;
      const rendered = await this.renderSequenceOffline(sequence, programsByName, resolveOverrides(sequence));
      onProgress?.(i + 1, entries.length);
      if (!rendered) continue;
      const offsetSamples = Math.round(offsetsSeconds[i] * sampleRate);
      for (let ch = 0; ch < 2; ch++) {
        const src = rendered.numberOfChannels > ch ? rendered.getChannelData(ch) : rendered.getChannelData(0);
        const dst = masterChannels[ch];
        const len = Math.min(src.length, totalSamples - offsetSamples);
        for (let s = 0; s < len; s++) dst[offsetSamples + s] += src[s];
      }
    }
    return master;
  }

  /**
   * Play a sequence. Returns a controller: { stop(), resolvedEvents,
   * loopLengthSeconds, startedAt, trackBuses }. onLoop(startAt) fires each
   * time a loop iteration is (re)scheduled, so the UI can drive a
   * playhead. `overrides` is a partial resolver (see DEFAULT_OVERRIDES)
   * for live level/pan/tuning/mute — typically the Pinia-backed one from
   * useOverridesStore. `trackBuses` (Map<trackNumber, busHandle>) lets the
   * caller adjust level/pan/mute for any active track *instantly*, at any
   * point during playback — see PadMixer/TrkMixer.
   * @param {any} sequence
   * @param {any} programsByName
   * @param {{ loop?: boolean, onLoop?: (startAt: number) => void, onEvent?: (event: any) => void, overrides?: object, startAt?: number }} [options]
   */
  playSequence(sequence, programsByName, { loop = true, onLoop, onEvent, overrides = {}, startAt: startAtOverride } = {}) {
    const ov = { ...DEFAULT_OVERRIDES, ...overrides };
    // Built once — sequence.events is static for this controller's whole
    // lifetime, so there's no need to re-scan it every beat the way
    // resolveEvents itself has to re-derive pad-level state every beat.
    const automationLanes = buildAutomationLanes(sequence.events);
    const baseSecondsPerTick = 60 / sequence.bpm / sequence.ticksPerBeat;
    const baseLoopLengthSeconds = sequence.bars * sequence.ticksPerBar * baseSecondsPerTick;
    // Updated every loop iteration below so a live varispeed change is
    // reflected in what the controller exposes (playhead tracking, the
    // queued-sequence-switch timing in useProjectStore's playback store).
    let currentLoopLengthSeconds = baseLoopLengthSeconds;

    const trackBuses = new Map();
    // Latest still-possibly-sounding audio-track source per track number —
    // see the audio-layer scheduling loop below for why a fresh retrigger
    // needs to explicitly stop the previous one instead of letting it run
    // out on its own.
    const audioLayerSources = new Map();
    const busFor = (trackNumber, track) => {
      let bus = trackBuses.get(trackNumber);
      if (!bus) {
        bus = this._createBus();
        bus.setLevel(ov.getTrackLevel(trackNumber, track));
        bus.setPan(ov.getTrackPan(trackNumber, track));
        if (!ov.isTrackAudible(trackNumber, track)) bus.setMuted(true);
        if (ov.isFxEnabled()) bus.setSend(ov.getTrackSend(trackNumber, track));
        trackBuses.set(trackNumber, bus);
      }
      return bus;
    };

    let stopped = false;
    let loopTimer = null;
    const activeSources = [];
    const eventTimers = [];
    const activeVoiceKeys = [];
    // Every currently-sounding voice's source + its rate with varispeed
    // *excluded* (tuning-only for notes, 1 for audio layers) — lets
    // setVarispeed() below retune whatever's already ringing out the
    // instant the knob moves, rather than only affecting the next loop's
    // freshly-scheduled notes (which could be many seconds away).
    const activeVoices = [];
    let liveVarispeed = 1;
    // Exposed on the returned controller for any external reads (e.g. UI
    // note counts) — always the latest loop's resolution.
    let latestResolvedEvents = [];
    let latestAudioLayers = [];

    // Scheduled one BEAT at a time (not the whole loop, and finer than a
    // whole bar) so live overrides — varispeed most of all, but also
    // pad-mixer level/pan/tuning — catch up within a fraction of a second
    // instead of waiting for a whole bar (~3s) or, worse, the *entire
    // loop* (an 8-16 bar sequence is easily 10-30+ seconds). Choosing
    // beat-sized chunks over even-smaller fixed windows keeps this simple
    // (ticksPerBeat is already a known constant) while still being tight
    // enough to feel live when turning the varispeed knob. Audio-track
    // layers are still scheduled once per loop (at beat 0): they're WAV
    // recordings spanning the whole loop, and being long-lived they're
    // already reached by setVarispeed's live voice retuning below —
    // chopping them into small pieces would need actual mid-file
    // re-triggering, real risk for no real benefit.
    const beatsPerLoop = (sequence.ticksPerBar / sequence.ticksPerBeat) * sequence.bars;
    // Which loop iteration (0-based, since this controller started) we're
    // on — needed because an audio-track recording can span more than one
    // loop's length (see the audio-layer triggering comment below), not
    // derived from elapsed time since that drifts if varispeed changes.
    const scheduleBeat = (beatIndex, loopStartAt, loopIndex) => {
      if (stopped) return;

      const varispeed = Math.max(0.5, Math.min(1.5, ov.getVarispeed() || 1));
      liveVarispeed = varispeed;
      const secondsPerTick = baseSecondsPerTick / varispeed;
      const loopLengthSeconds = baseLoopLengthSeconds / varispeed;
      currentLoopLengthSeconds = loopLengthSeconds;

      // Browsers throttle setTimeout in backgrounded/inactive tabs, so this
      // recursive per-beat chain can fall behind real AudioContext time by
      // several beats before it next runs. Left unchecked, every stale
      // intermediate beat still gets scheduled with `when = loopStartAt +
      // offsetSeconds` — a time already in the past by the time this
      // actually executes — and Web Audio starts a past-due source
      // immediately, so a backlog plays as a rapid-fire burst of notes
      // racing to catch up. Meanwhile any audio-track buffer, already
      // started on its own independent clock back when it was first
      // scheduled, keeps playing at normal speed regardless — which is
      // exactly "MIDI plays way too fast on top of the audio." Detect
      // falling more than one beat behind and jump straight to the beat
      // we should actually be on (from elapsed real time) instead of
      // honoring every missed one.
      const beatDurationSeconds = sequence.ticksPerBeat * secondsPerTick;
      const nominalStart = loopStartAt + beatIndex * beatDurationSeconds;
      const behindBy = this.ctx.currentTime - nominalStart;
      if (behindBy > beatDurationSeconds) {
        const beatsSinceLoopStart = Math.floor((this.ctx.currentTime - loopStartAt) / beatDurationSeconds);
        const targetBeat = Math.max(beatIndex, beatsSinceLoopStart);
        const loopsToSkip = Math.floor(targetBeat / beatsPerLoop);
        scheduleBeat(targetBeat % beatsPerLoop, loopStartAt + loopsToSkip * loopLengthSeconds, loopIndex + loopsToSkip);
        return;
      }

      // Resolved fresh every beat so latestResolvedEvents (UI note counts
      // etc.) always reflects the full loop, even though only this beat's
      // slice actually gets scheduled below. Song mode now chains the source
      // sequences directly, so this path stays on the sequence timing model
      // that direct playback already used.
      const resolvedEvents = this.resolveEvents(sequence, programsByName, secondsPerTick, automationLanes);
      latestResolvedEvents = resolvedEvents;

      const beatStartTick = beatIndex * sequence.ticksPerBeat;
      const beatEndTick = beatStartTick + sequence.ticksPerBeat;
      const beatEvents = resolvedEvents.filter((re) => re.tick >= beatStartTick && re.tick < beatEndTick);

      for (const re of beatEvents) {
        const when = loopStartAt + re.offsetSeconds;
        const bus = busFor(re.track.number, re.track);
        const p = this._notePlaybackParams(re, ov, when);
        const src = this._playOne(
          re.buffer,
          {
            gain: p.gain,
            pan: p.pan,
            playbackRate: p.tuningRate * varispeed,
            reverse: p.reverse,
            attack: p.attack,
            decay: p.decay,
            decayMode: p.decayMode,
            filters: p.filters,
            filterInputGain: p.filterInputGain,
            trim: p.trim,
            chokeKeys: p.chokeKeys,
          },
          when,
          p.stopAt,
          bus.input
        );
        activeSources.push(src);
        activeVoices.push({ src, baseRate: p.tuningRate });
        for (const key of p.chokeKeys) activeVoiceKeys.push({ key, src });
        if (onEvent) {
          const delayMs = Math.max(0, (when - this.ctx.currentTime) * 1000);
          eventTimers.push(setTimeout(() => onEvent(re), delayMs));
        }
      }

      if (beatIndex === 0) {
        const audioLayers = this.resolveAudioTracks(sequence);
        latestAudioLayers = audioLayers;
        for (const layer of audioLayers) {
          // An audio-track recording doesn't have to be exactly one loop
          // long — real MPC audio-track overdubs can run continuously
          // across several loop passes without stopping (confirmed on
          // real projects: many takes land within ~1% of a clean 2x or 4x
          // multiple of their sequence's loop length, not a coincidence).
          // Re-triggering it fresh at every single loop start would cut
          // it off partway through and restart it, instead of letting it
          // play through its own full natural length — so only (re)start
          // it once every `triggerEveryN` loop passes, matching however
          // many loops its own duration actually spans.
          const triggerEveryN = Math.max(1, Math.round(layer.buffer.duration / loopLengthSeconds));
          if (loopIndex % triggerEveryN !== 0) continue;
          const bus = busFor(layer.track.number, layer.track);
          // A real WAV export is almost never an exact round multiple of
          // the loop length (these two are typically a few ms apart) — with
          // no explicit stop, the previous copy would keep ringing briefly
          // into the new loop while a fresh copy starts at the same
          // instant, an audible doubling/"flam" at every single loop
          // boundary. Stop it right at this loop's start instead, same
          // sample-accurate-boundary approach as the queued sequence
          // switch in playback.ts.
          const prevSrc = audioLayerSources.get(layer.track.number);
          if (prevSrc) {
            try {
              prevSrc.stop(loopStartAt);
            } catch {
              /* already stopped/ended */
            }
          }
          const src = this._playOne(layer.buffer, { gain: 1, playbackRate: varispeed }, loopStartAt, null, bus.input);
          audioLayerSources.set(layer.track.number, src);
          activeSources.push(src);
          activeVoices.push({ src, baseRate: 1 });
        }
        if (onLoop) onLoop(loopStartAt, loopLengthSeconds);
      }

      // Recorded Track Mixer automation (CC#7/10/91 — see the constants'
      // comment above): applied to every bus that exists so far this
      // beat, live, the same way a user's own fader drag would be. Only
      // touches a track the user hasn't manually adjusted this session —
      // once they do, that live edit is the more specific, more recent
      // intent and keeps winning over whatever was recorded. CC#91 (send)
      // only takes effect at all if FX sends are enabled — see isFxEnabled.
      for (const [trackNumber, bus] of trackBuses) {
        const ccLanes = automationLanes.ccByTrack.get(trackNumber);
        if (!ccLanes) continue;
        if (!ov.hasTrackLevelOverride(trackNumber)) {
          const ccLevel = lookupAtOrBefore(ccLanes.get(CC_TRACK_LEVEL), beatStartTick)?.value;
          if (ccLevel != null) bus.setLevel(ccLevel);
        }
        if (!ov.hasTrackPanOverride(trackNumber)) {
          const ccPan = lookupAtOrBefore(ccLanes.get(CC_TRACK_PAN), beatStartTick)?.value;
          if (ccPan != null) bus.setPan(Math.round((ccPan * 100) / 127));
        }
        if (ov.isFxEnabled() && !ov.hasTrackSendOverride(trackNumber)) {
          const ccSend = lookupAtOrBefore(ccLanes.get(CC_TRACK_SEND), beatStartTick)?.value;
          if (ccSend != null) bus.setSend(Math.round((ccSend * 100) / 127));
        }
      }

      const delayMs = Math.max(0, (beatDurationSeconds - 0.05) * 1000);
      loopTimer = setTimeout(() => {
        if (stopped) return;
        const nextBeat = beatIndex + 1;
        if (nextBeat < beatsPerLoop) {
          scheduleBeat(nextBeat, loopStartAt, loopIndex);
        } else if (loop) {
          scheduleBeat(0, loopStartAt + loopLengthSeconds, loopIndex + 1);
        }
      }, delayMs);
    };

    const startedAt = startAtOverride ?? this.ctx.currentTime + 0.06;
    scheduleBeat(0, startedAt, 0);

    return {
      get resolvedEvents() {
        return latestResolvedEvents;
      },
      get audioLayers() {
        return latestAudioLayers;
      },
      get loopLengthSeconds() {
        return currentLoopLengthSeconds;
      },
      startedAt,
      trackBuses,
      /**
       * Retune every currently-sounding voice to a new varispeed value
       * immediately — called from useOverridesStore.setVarispeed so
       * dragging the knob has an audible effect right away instead of
       * waiting for the next loop pass (which recomputes it anyway, for
       * whatever gets scheduled after this call).
       */
      setVarispeed: (value) => {
        liveVarispeed = Math.max(0.5, Math.min(1.5, value || 1));
        for (const voice of activeVoices) {
          try {
            voice.src.playbackRate.setTargetAtTime(voice.baseRate * liveVarispeed, this.ctx.currentTime, 0.01);
          } catch {
            /* voice already ended */
          }
        }
      },
      /**
       * `at`, if given, is an exact AudioContext time to stop on — used for
       * a queued sequence switch, so the outgoing sequence's cutoff lands
       * on the *same* sample-accurate boundary the incoming sequence's
       * `startAt` already uses (see playback.ts's queueSwitch), instead of
       * "now" as evaluated whenever the JS setTimeout callback that called
       * this happens to actually fire — ordinary timer jitter (a few ms
       * either way) was the last source of an audible gap/overlap at the
       * loop boundary. Bus teardown is deferred past `at` too: disposing
       * immediately would sever the connection before the precisely-timed
       * source stop even lands, undoing the whole point.
       */
      stop: (at) => {
        stopped = true;
        if (loopTimer) clearTimeout(loopTimer);
        for (const t of eventTimers) clearTimeout(t);
        const stopTime = at ?? this.ctx.currentTime;
        for (const src of activeSources) {
          try {
            src.stop(stopTime);
          } catch {
            /* already stopped/ended */
          }
        }
        const cleanup = () => {
          for (const bus of trackBuses.values()) bus.dispose();
          for (const { key, src } of activeVoiceKeys) {
            if (this.voices.get(key)?.src === src) this.voices.delete(key);
          }
        };
        const waitMs = (stopTime - this.ctx.currentTime) * 1000;
        if (waitMs > 0) setTimeout(cleanup, waitMs + 20);
        else cleanup();
      },
    };
  }
}
